/**
 * Spell VFX director for the toon renderer. Turns sim events (cast start, cast, damage, heal,
 * aura apply/remove, death, interrupt, dispel, telegraph end) and zone areas into layered effects:
 *  - pooled instanced GPU particles (fx/pool.ts, fx/particles.ts) under a per-region overdraw
 *    budget that drops the lowest-priority emitters first (fx/budget.ts, fx/emitters.ts);
 *  - ribbon trails, weapon swing arcs, jagged lightning and beams (fx/strips.ts);
 *  - ground decals: shockwave rings, scorch, frost, holy glyphs, cracks, telegraph slams (fx/decals.ts);
 *  - light pillars, vortices, charged auras and bubbles (fx/volumes.ts);
 *  - light camera shake and hit-stop on big crits (fx/shake.ts).
 * What to draw per ability comes from the data table in fx/table.ts (school/kind fallbacks for
 * anything unmapped). Everything scales with the effects-intensity setting; impact comes from
 * motion, shape and timing at moderate brightness.
 */
import * as THREE from 'three';
import type { ContentRegistry, SimEvent, UnitId, WorldShape } from '@keystone/sim';
import { ABILITY, BASE_ABILITY } from '@keystone/content';
import { AUTO_SHOT_ABILITY_ID, MELEE_ABILITY_ID } from '@keystone/sim';
import type { RenderArea, RenderFrame, RenderFx, RenderUnit } from '../types';
import { PRIO } from './fx/budget';
import { DECAL, DecalBatch } from './fx/decals';
import { type EmitReq, Emitters, P, type Preset, REGIONS, tint } from './fx/emitters';
import { clamp01, easeOutCubic, samplePointInShape, shapeArea, shapeCenter } from './fx/geom';
import { ParticleBatch } from './fx/particles';
import { ParticlePool } from './fx/pool';
import { CameraShake, HitStop } from './fx/shake';
import { StripBatch } from './fx/strips';
import {
  ABILITY_VFX,
  type AbilityInfo,
  type AuraVfx,
  C,
  FRIENDLY_ALPHA,
  type ImpactStyle,
  type RGB,
  type TickStyle,
  type VfxSpec,
  type ZoneStyle,
  resolveAuraVfx,
  resolveVfx,
  schoolColors,
  SPEC_AUTO_VFX, // +ranger-specs
} from './fx/table';
import { createFxAtlas } from './fx/textures';
import type { FxCueId } from './anim/clips';
import { VOL, VolumeBatch } from './fx/volumes';
import { groundY } from './ground';
import { CloneFx, type CloneBurst } from './fx/clones'; // +rogue-specs: Silhouettes / Phantoms

/** What the director needs from the renderer each frame. */
export interface FxView {
  units: ReadonlyMap<UnitId, RenderUnit>;
  heightOf(id: UnitId): number;
  /** Casting hand in world space, or null (off-screen / unknown). */
  handOf(id: UnitId, out: THREE.Vector3): THREE.Vector3 | null;
  camera: THREE.Camera;
  playerId: UnitId | null;
  /** Camera focus on the sim plane (shake falloff). */
  focus: { x: number; y: number };
}

// --- scratch ---------------------------------------------------------------
const V1 = new THREE.Vector3();
const V2 = new THREE.Vector3();
const V3 = new THREE.Vector3();
const V4 = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
const P2 = { x: 0, y: 0 };
const ZERO = new THREE.Vector3();
const SMOKE: RGB = [0.11, 0.1, 0.12];
const GUNSMOKE: RGB = [0.62, 0.6, 0.56]; // +ranger-specs: the Deadeye's pale gunsmoke
const DUST: RGB = [0.5, 0.44, 0.38];
const DEBRIS: RGB = [0.42, 0.38, 0.36];
const SCORCH: RGB = [0.05, 0.035, 0.03];
const CRACK: RGB = [0.04, 0.035, 0.05];
const rnd = Math.random;

/** Unit position at height `h` above the floor under it (the floor may be raised). */
const toThree = (u: RenderUnit, h: number, out: THREE.Vector3): THREE.Vector3 => out.set(u.pos.x, h + groundY(u.pos.x, u.pos.y), -u.pos.y);
/** Floor height under a three.js (x, z) point. */
const G = (x: number, z: number): number => groundY(x, -z);

// --- live effect records ----------------------------------------------------
export interface Hit {
  kind: 'damage' | 'heal';
  targetId: UnitId;
  crit: boolean;
  tick: boolean;
  amount: number;
  /** +impact: the hit killed its target (overkill > 0). */
  kill: boolean;
  /** +impact: an AoE hit (one of many targets). */
  aoe: boolean;
  /** The sim event behind a deferred hit; its combat text is held until the hit lands. */
  ev?: SimEvent;
}

/**
 * +impact: called when a hit's visual lands (projectiles on arrival, chains per hop, melee at the
 * clip's contact frame), so sound, flinch and camera punch fire on the same frame as the impact.
 */
export type LandListener = (abilityId: string, spec: VfxSpec, sourceId: UnitId | undefined, hit: Hit, parried: boolean, view: FxView) => void;

interface Projectile {
  spec: VfxSpec;
  abilityId: string;
  sourceId: UnitId;
  targetId: UnitId;
  from: THREE.Vector3;
  last: THREE.Vector3;
  t: number;
  dur: number;
  arc: number;
  hits: Hit[];
  strip: number;
  hist: Float32Array;
  histN: number;
  histHead: number;
  arrived: boolean;
  fade: number;
  acc: number;
  prio: number;
}

interface Bolt {
  alpha: number;
  strip: number;
  branch: number;
  a: THREE.Vector3;
  b: THREE.Vector3;
  age: number;
  life: number;
  jitter: number;
  width: number;
}

interface Swing {
  strip: number;
  c: THREE.Vector3;
  f: THREE.Vector3;
  r: THREE.Vector3;
  style: NonNullable<VfxSpec['swing']>;
  rad: number;
  age: number;
  life: number;
  width: number;
}

interface Beam {
  strip: number;
  a: THREE.Vector3;
  b: THREE.Vector3;
  sourceId: UnitId | null;
  targetId: UnitId | null;
  age: number;
  life: number;
  width: number;
  dash: boolean;
}

interface CastLoop {
  unitId: UnitId;
  spec: VfxSpec;
  until: number;
  acc: number;
  pulse: number;
  dead: boolean;
}

interface AuraLoop {
  key: string;
  unitId: UnitId;
  vfx: AuraVfx;
  hostileSrc: boolean;
  vol: number;
  vol2: number;
  decal: number;
  acc: number;
  acc2: number;
  until: number;
  dead: boolean;
}

interface ZoneFx {
  id: string;
  style: ZoneStyle | null;
  spec: VfxSpec | null;
  area: WorldShape;
  decal: number;
  decal2: number;
  acc: number;
  pulse: number;
  seen: number;
  /** +basekits: smoothed position of a travelling zone (Wandering Star's orb), sim plane. */
  sx?: number;
  sy?: number;
  /** +basekits: the orb's held core / halo volumes (-1 = none). */
  vol?: number;
  vol2?: number;
}

/** +basekits: Wandering Star's zone def id (its orb, arcs and end bloom are drawn here). */
const STAR_ZONE = 'mage.wandering_star';

interface Scheduled {
  at: number;
  run: () => void;
}

const SWING_POINTS = 14;
/** +basekits: Wandering Star's orb height above the ground (yd). */
const STAR_H = 1.5;
const TRAIL_POINTS = 18;
const BOLT_POINTS = 16;
/** Concurrent lightning arcs (each is 1–2 strips). */
const MAX_BOLTS = 28;
/** +impact: longest a melee hit waits for its clip's contact frame (s). */
const MAX_CONTACT_HOLD = 0.42;

export class ToonFx {
  readonly group = new THREE.Group();
  private readonly atlas: THREE.CanvasTexture;
  private readonly uFx = { value: 1 };
  private readonly uTime = { value: 0 };
  private readonly addPool = new ParticlePool(6000, REGIONS);
  private readonly alphaPool = new ParticlePool(1600, REGIONS);
  private readonly addBatch: ParticleBatch;
  private readonly alphaBatch: ParticleBatch;
  private readonly emit: Emitters;
  private readonly trails: StripBatch;
  private readonly arcs: StripBatch;
  private readonly decalAdd: DecalBatch;
  private readonly decalAlpha: DecalBatch;
  private readonly cylGeo = new THREE.CylinderGeometry(1, 1, 1, 24, 1, true).translate(0, 0.5, 0);
  private readonly sphGeo = new THREE.SphereGeometry(1, 28, 18);
  private readonly cyl: VolumeBatch;
  private readonly sph: VolumeBatch;
  readonly shake = new CameraShake();
  readonly hitStop = new HitStop();
  /** +basekits: a Wandering Star burst went off at (x, y) on the sim plane (index.ts plays its sound). */
  onStarBurst: ((x: number, y: number) => void) | null = null;

  private projectiles: Projectile[] = [];
  private bolts: Bolt[] = [];
  private swings: Swing[] = [];
  private beams: Beam[] = [];
  private casts: CastLoop[] = [];
  private auras: AuraLoop[] = [];
  private readonly auraIndex = new Map<string, AuraLoop>();
  private readonly zones = new Map<string, ZoneFx>();
  private zoneList: ZoneFx[] = [];
  private readonly telegraphs = new Map<string, { area: WorldShape; abilityId: string; sourceId: UnitId }>();
  private scheduled: Scheduled[] = [];
  private readonly pending = new Map<string, Hit[]>();
  /** Combat-text sync: events whose hit is still in flight (→ fx time they were held). */
  private readonly heldText = new Map<SimEvent, number>();
  /** Events the HUD should show as text this frame (not held, or just landed). Reused. */
  private readonly textOut: SimEvent[] = [];
  private readonly infoCache = new Map<string, AbilityInfo | null>();
  private readonly lastAbsorb = new Map<UnitId, number>();
  private now = 0;
  private frameNo = 0;
  private k = 1;
  /**
   * Units whose ability clip is drawing its own weapon trail right now (render/toon/anim): the
   * synthetic swing arc is skipped for them. Set by the renderer.
   */
  ownsSwing: (id: UnitId) => boolean = () => false;
  /**
   * +impact: seconds until `id`'s playing strike clip for `abilityId` reaches its contact frame
   * (first hit-stop marker), or -1. Melee hits are held until then so the impact, its sound and
   * the enemy's flinch land when the blade does. Set by the renderer.
   */
  contactIn: (id: UnitId, abilityId: string) => number = () => -1;
  /** +impact: hit landed (see LandListener). */
  onLand: LandListener | null = null;
  /**
   * +impact: a killing blow is in flight (held melee contact, projectile, chain hop): the unit
   * view holds its death fall for at most `maxS`, until `onReleaseDeath` (the blow landed).
   */
  onDeferDeath: ((id: UnitId, maxS: number) => void) | null = null;
  onReleaseDeath: ((id: UnitId) => void) | null = null;
  /** +impact: targets whose killing blow is held, and deaths waiting for it (→ fx time, killer). */
  private readonly killHeld = new Set<UnitId>();
  private readonly deathWait = new Map<UnitId, { at: number; killer: UnitId | undefined }>();
  /** +rogue-specs: one translucent figure per Silhouette / Phantom stack (fx/clones.ts). */
  private readonly clones = new CloneFx((kind, x, y, h, c, a) => this.cloneBurst(kind, x, y, h, c, a));

  constructor(private readonly content: ContentRegistry | null) {
    this.atlas = createFxAtlas();
    this.alphaBatch = new ParticleBatch(this.alphaPool, this.atlas, this.uFx, false, 8);
    this.addBatch = new ParticleBatch(this.addPool, this.atlas, this.uFx, true, 10);
    this.emit = new Emitters(this.addPool, this.alphaPool);
    const su = { uFx: this.uFx, uTime: this.uTime };
    this.trails = new StripBatch(56, TRAIL_POINTS, su, true, 9);
    this.arcs = new StripBatch(64, BOLT_POINTS, su, true, 9);
    // Ground decals sit under telegraphs (areas.ts uses renderOrder 2–3).
    this.decalAlpha = new DecalBatch(64, this.uFx, false, 1.4);
    this.decalAdd = new DecalBatch(112, this.uFx, true, 1.6);
    this.cyl = new VolumeBatch(40, this.cylGeo, this.uFx, 8.5);
    this.sph = new VolumeBatch(20, this.sphGeo, this.uFx, 8.6);
    this.group.add(this.decalAlpha.mesh, this.decalAdd.mesh, this.alphaBatch.mesh, this.cyl.mesh, this.sph.mesh, this.trails.mesh, this.arcs.mesh, this.addBatch.mesh);
    this.group.add(this.arrows);
    this.group.add(this.clones.group); // +rogue-specs
  }

  /** +rogue-specs: particles for the shadow clones (fx/clones.ts). */
  private cloneBurst(...[kind, x, y, h, c, a]: Parameters<CloneBurst>): void {
    const p = V1.set(x, groundY(x, y) + h, -y);
    if (kind === 'smoke') this.burst(P.smoke, PRIO.support, 5, p, SMOKE, SMOKE, 0.9, 0.5).radius = 0.5;
    else if (kind === 'shards') this.burst(P.shards, PRIO.impact, 10, p, c, a, 0.9, FRIENDLY_ALPHA).radius = 0.6;
    else if (kind === 'ring') this.burst(P.ring, PRIO.support, 1, p, c, a, 1.6, FRIENDLY_ALPHA);
    else {
      this.burst(P.crescent, PRIO.impact, 1, p, c, a, 1.4, 1);
      this.burst(P.sparks, PRIO.impact, 8, p, c, a, 0.9, FRIENDLY_ALPHA);
    }
  }

  /** +classes: real arrow meshes for 'arrow' projectiles (the Ranger): one instanced draw. */
  private readonly arrowGeo = arrowGeometry();
  private readonly arrowMat = new THREE.MeshBasicMaterial({ vertexColors: true });
  private readonly arrows = (() => {
    const m = new THREE.InstancedMesh(this.arrowGeo, this.arrowMat, ARROW_MAX);
    m.count = 0;
    m.frustumCulled = false;
    m.name = 'fx-arrows';
    return m;
  })();
  private arrowN = 0;

  /** Effects intensity (0.2..1.5): alpha, particle counts, shake and hit-stop all scale with it. */
  set intensity(k: number) {
    this.k = Math.max(0, k);
    this.uFx.value = Math.min(1.2, this.k);
    this.emit.countScale = Math.max(0.3, Math.min(1.25, 0.35 + 0.65 * this.k));
    this.shake.intensity = this.k;
    this.hitStop.intensity = this.k;
  }

  get intensity(): number {
    return this.k;
  }

  set shakeEnabled(on: boolean) {
    this.shake.enabled = on;
    if (!on) this.shake.trauma = 0;
  }

  /** Real frame time in → simulation time for animation/effects out (hit-stop). */
  beginFrame(dtS: number): number {
    this.shake.update(dtS);
    return dtS * this.hitStop.update(dtS);
  }

  /** Camera offset (yards along camera right/up). */
  shakeOffset(out: { x: number; y: number }): { x: number; y: number } {
    return this.shake.offset(out);
  }

  // =========================================================================
  // Event intake
  // =========================================================================

  ingest(frame: RenderFrame, view: FxView): void {
    this.frameNo++;
    for (const a of frame.areas) {
      if (a.kind === 'telegraph') {
        const t = this.telegraphs.get(a.id);
        if (t) t.area = a.area;
      }
    }
    this.syncZones(frame.areas);
    this.textOut.length = 0;
    const events = frame.events;
    if (this.k <= 0.01 || !events) {
      if (events) for (const e of events) this.textOut.push(e);
      if (this.k > 0.01) for (const f of frame.newFx) this.legacy(f, view);
      return;
    }
    this.pending.clear();
    for (const e of events) {
      this.clones.onEvent(e, view.units); // +rogue-specs
      this.onEvent(e, view);
      if (!this.heldText.has(e)) this.textOut.push(e);
    }
  }

  /**
   * Combat events for floating text this frame, in sync with the visuals: projectile, chain and
   * wave hits are held until their effect lands, so crit text pops on the same frame as the
   * impact, hit-stop and shake. Valid until the next `ingest`.
   */
  textEvents(): readonly SimEvent[] {
    return this.textOut;
  }

  private hold(hit: Hit, e: SimEvent): void {
    hit.ev = e;
    this.heldText.set(e, this.now);
  }

  private land(hit: Hit): void {
    const e = hit.ev;
    if (!e) return;
    hit.ev = undefined;
    if (this.heldText.delete(e)) this.textOut.push(e);
  }

  private info(abilityId: string): AbilityInfo | null {
    let i = this.infoCache.get(abilityId);
    if (i !== undefined) return i;
    const a = this.content?.ability(abilityId);
    const ea = this.content?.enemyAbility(abilityId);
    i = a
      ? {
          school: a.school ?? null,
          kind: ea?.kind,
          targeting: a.targeting,
          melee: a.melee,
          range: a.range,
          channel: a.cast?.type === 'channel',
          castTime: a.cast?.type === 'cast',
        }
      : null;
    this.infoCache.set(abilityId, i);
    return i;
  }

  private hostile(id: UnitId | undefined, view: FxView): boolean {
    if (!id) return false;
    const u = view.units.get(id);
    return !!u && u.faction !== 'party';
  }

  private spec(abilityId: string, sourceId: UnitId | undefined, view: FxView): VfxSpec {
    const melee = abilityId === MELEE_ABILITY_ID;
    // +ranger-specs: Auto Shot looks like the spec's weapon (the Deadeye's pistol fires bullets).
    if (abilityId === AUTO_SHOT_ABILITY_ID && sourceId) {
      const sid = view.units.get(sourceId)?.specId;
      const own = sid ? SPEC_AUTO_VFX[sid] : undefined;
      if (own) return own;
    }
    return resolveVfx(abilityId, melee ? null : this.info(abilityId), this.hostile(sourceId, view), melee);
  }

  private prio(sourceId: UnitId | undefined, view: FxView, base: number): number {
    return sourceId && sourceId === view.playerId ? Math.max(base, PRIO.player) : base;
  }

  private onEvent(e: SimEvent, view: FxView): void {
    switch (e.type) {
      case 'begincast': {
        const spec = this.spec(e.abilityGameID, e.sourceID, view);
        if (spec.cast !== 'none') this.startCast(e.sourceID, spec, e.duration / 1000);
        break;
      }
      case 'cast':
        this.endCast(e.sourceID);
        this.release(e.sourceID, e.targetID, e.abilityGameID, view);
        break;
      case 'cast_cancelled':
        this.endCast(e.sourceID);
        break;
      case 'damage': {
        // +autoattack: Auto Shot has no cast event: its damage looses the arrow (the hit rides it).
        if (e.abilityGameID === AUTO_SHOT_ABILITY_ID && !e.tick) this.release(e.sourceID, e.targetID, e.abilityGameID, view);
        if (e.hitType === 'miss' || e.hitType === 'dodge' || e.hitType === 'immune') break;
        if (e.abilityGameID === BASE_ABILITY.mage.wanderingStar) this.starArc(e.targetID, view); // +basekits
        const hit: Hit = { kind: 'damage', targetId: e.targetID, crit: e.hitType === 'crit', tick: !!e.tick, amount: e.amount + e.absorbed, kill: e.overkill > 0, aoe: e.isAoE };
        const group = this.pending.get(`${e.sourceID}|${e.abilityGameID}`);
        if (group && !e.tick) {
          group.push(hit);
          this.hold(hit, e);
          if (hit.kill) this.holdKill(e.targetID, 0.9);
          break;
        }
        // +impact: melee waits for the blade (the strike clip's contact frame).
        const wait = e.tick ? -1 : this.contactIn(e.sourceID, e.abilityGameID);
        if (wait > 0.01) {
          const d = Math.min(MAX_CONTACT_HOLD, wait);
          this.hold(hit, e);
          if (hit.kill) this.holdKill(e.targetID, d + 0.05);
          const src = e.sourceID;
          const ability = e.abilityGameID;
          const parried = e.hitType === 'parry';
          this.schedule(d, () => this.applyHit(ability, src, hit, view, parried));
        } else this.applyHit(e.abilityGameID, e.sourceID, hit, view, e.hitType === 'parry');
        break;
      }
      case 'heal': {
        if (e.amount <= 0 && e.overheal <= 0) break;
        const hit: Hit = { kind: 'heal', targetId: e.targetID, crit: e.hitType === 'crit', tick: !!e.tick, amount: e.amount, kill: false, aoe: false };
        const group = this.pending.get(`${e.sourceID}|${e.abilityGameID}`);
        if (group && !e.tick) {
          group.push(hit);
          this.hold(hit, e);
        }
        else this.applyHit(e.abilityGameID, e.sourceID, hit, view, false);
        break;
      }
      case 'absorbed': {
        const last = this.lastAbsorb.get(e.targetID) ?? -1;
        if (this.now - last < 0.3) break;
        this.lastAbsorb.set(e.targetID, this.now);
        const u = view.units.get(e.targetID);
        if (!u) break;
        toThree(u, view.heightOf(u.id) * 0.55, V1);
        this.burst(P.ring, PRIO.support, 1, V1, C.shield, C.shieldCore, 0.9, FRIENDLY_ALPHA * 0.7);
        break;
      }
      case 'applybuff':
      case 'applydebuff':
        this.startAura(e.targetID, e.abilityGameID, e.sourceID, view);
        break;
      case 'removebuff':
      case 'removedebuff':
        this.endAura(`${e.targetID}|${e.abilityGameID}`);
        break;
      case 'interrupt':
        this.endCast(e.targetID);
        this.shatter(e.targetID, e.sourceID, view);
        break;
      case 'dispel': {
        const spec = this.spec(e.abilityGameID, e.sourceID, view);
        this.impactOn('cleanse', spec, e.targetID, e.sourceID, false, view, PRIO.support);
        this.endAura(`${e.targetID}|${e.extraAbilityGameID}`);
        break;
      }
      case 'death': {
        // +impact: a held killing blow plays the death when the blow lands (see applyHit).
        if (this.killHeld.has(e.targetID)) this.deathWait.set(e.targetID, { at: this.now, killer: e.sourceID });
        else this.death(e.targetID, view, e.sourceID);
        break;
      }
      case 'summon': {
        const u = view.units.get(e.targetID);
        if (!u) break;
        toThree(u, 0.5, V1);
        this.burst(P.smoke, PRIO.impact, 6, V1, SMOKE, SMOKE, 1.0, 0.6).radius = 0.6;
        this.burst(P.ring, PRIO.impact, 1, V1, C.hostilePhys, C.hostilePhysCore, 1.0, 0.7);
        break;
      }
      case 'telegraph_start':
        if (this.telegraphs.size > 256) this.telegraphs.clear();
        this.telegraphs.set(e.telegraphId, { area: e.area, abilityId: e.abilityGameID, sourceId: e.sourceID });
        break;
      case 'telegraph_end': {
        const t = this.telegraphs.get(e.telegraphId);
        this.telegraphs.delete(e.telegraphId);
        if (t) this.slam(t.area, this.spec(t.abilityId, t.sourceId, view), view);
        break;
      }
      default:
        break;
    }
  }

  /** newFx-only fallback (no raw events on the frame). */
  private legacy(f: RenderFx, view: FxView): void {
    const hostile = this.hostile(f.sourceId ?? undefined, view);
    const [color, accent] = schoolColors(f.school, hostile);
    const spec = resolveVfx(`school:${f.school ?? 'physical'}`, { school: f.school, targeting: 'enemy', range: 30 }, hostile);
    if (!f.targetId) return;
    switch (f.kind) {
      case 'projectile':
        if (f.sourceId) this.launch(spec, spec.key, f.sourceId, f.targetId, [], view);
        break;
      case 'hit':
      case 'crit':
        this.impactOn(spec.impact, { ...spec, color, accent }, f.targetId, f.sourceId ?? undefined, f.kind === 'crit', view, PRIO.impact);
        break;
      case 'heal':
        this.impactOn('heal', spec, f.targetId, f.sourceId ?? undefined, false, view, PRIO.support);
        break;
      case 'death':
        this.death(f.targetId, view);
        break;
      case 'interrupt':
        this.shatter(f.targetId, f.sourceId ?? undefined, view);
        break;
      default:
        break;
    }
  }

  // =========================================================================
  // Release (cast success)
  // =========================================================================

  private release(sourceId: UnitId, targetId: UnitId | undefined, abilityId: string, view: FxView): void {
    const spec = this.spec(abilityId, sourceId, view);
    const src = view.units.get(sourceId);
    if (!src) return;
    const prio = this.prio(sourceId, view, spec.hostile ? PRIO.impact : PRIO.support + 1);
    const hand = view.handOf(sourceId, V1) ?? toThree(src, view.heightOf(sourceId) * 0.6, V1);
    const key = `${sourceId}|${abilityId}`;
    const tgt = targetId && targetId !== sourceId ? view.units.get(targetId) : undefined;
    switch (spec.release) {
      case 'projectile':
        if (tgt) {
          const hits: Hit[] = [];
          this.pending.set(key, hits);
          this.launch(spec, abilityId, sourceId, tgt.id, hits, view);
        }
        this.burst(P.flash, prio, 1, hand, spec.color, spec.accent, 0.5, this.alphaOf(spec));
        if (spec.projectile === 'bullet') this.muzzle(hand, tgt, spec, prio);
        break;
      case 'bolt':
        if (tgt) {
          toThree(tgt, view.heightOf(tgt.id) * 0.55, V2);
          this.bolt(hand, V2, spec, 0.24, 0.3);
        }
        this.burst(P.flash, prio, 1, hand, spec.color, spec.accent, 0.7, this.alphaOf(spec));
        break;
      case 'chain':
        if (tgt) {
          const hits: Hit[] = [];
          this.pending.set(key, hits);
          this.schedule(0, () => this.chain(spec, abilityId, sourceId, tgt.id, hits, view));
        }
        this.burst(P.flash, prio, 1, hand, spec.color, spec.accent, 0.7, this.alphaOf(spec));
        break;
      case 'swing':
        // A hero whose ability clip draws a real blade trail doesn't get the synthetic arc too.
        if (!this.ownsSwing(sourceId)) this.swing(src, spec, view);
        break;
      case 'nova':
        this.nova(src, spec, view, prio);
        break;
      case 'wave': {
        const hits: Hit[] = [];
        this.pending.set(key, hits);
        this.wave(src, spec, abilityId, hits, view);
        break;
      }
      case 'beam':
        if (tgt) {
          toThree(tgt, view.heightOf(tgt.id) * 0.55, V2);
          this.beam(hand, V2, spec, sourceId, tgt.id, 0.45, 0.24, false);
        }
        this.burst(P.flash, prio, 1, hand, spec.color, spec.accent, 0.6, this.alphaOf(spec));
        break;
      case 'charge':
        if (tgt) {
          toThree(src, view.heightOf(sourceId) * 0.45, V1);
          toThree(tgt, view.heightOf(tgt.id) * 0.45, V2);
          this.beam(V1, V2, spec, null, null, 0.32, 0.9, true);
          V3.subVectors(V2, V1);
          const len = V3.length();
          for (let i = 0; i < 5; i++) {
            V4.copy(V1).addScaledVector(V3, (i + 0.5) / 5);
            V4.y = G(V4.x, V4.z) + 0.3;
            this.burst(P.dust, PRIO.decor, 2, V4, DUST, DUST, 0.8, 0.6).radius = 0.4;
          }
          const q = this.burst(P.streaks, prio, Math.min(16, 4 + len), V2, spec.color, spec.accent, 1.2, this.alphaOf(spec));
          q.dx = -V3.x;
          q.dy = 0;
          q.dz = -V3.z;
          q.radius = 0.5;
        }
        break;
      case 'flash': {
        this.burst(P.flash, prio, 1, hand, spec.color, spec.accent, 0.75, this.alphaOf(spec));
        const q = this.burst(P.sparks, prio, 5, hand, spec.color, spec.accent, 0.7, this.alphaOf(spec));
        q.speed = 0.4;
        break;
      }
      case 'none':
        break;
    }
  }

  /**
   * +ranger-specs: a gunshot's muzzle flash: a hot white-gold bloom at the barrel, a cone of sparks
   * toward the target and a puff of gunsmoke that drifts up.
   */
  private muzzle(hand: THREE.Vector3, tgt: { pos: { x: number; y: number } } | undefined, spec: VfxSpec, prio: number): void {
    const al = this.alphaOf(spec);
    this.burst(P.flash, prio, 1, hand, spec.accent, spec.accent, 0.95 * spec.scale, al).life = 0.09;
    this.burst(P.glow, prio, 1, hand, spec.color, spec.accent, 1.1 * spec.scale, al * 0.8).life = 0.12;
    const q = this.burst(P.streaks, prio, 6, hand, spec.color, spec.accent, 0.7 * spec.scale, al);
    if (tgt) {
      q.dx = tgt.pos.x - hand.x;
      q.dy = 0;
      q.dz = -tgt.pos.y - hand.z; // sim y → three -z
    }
    q.radius = 0.12;
    q.speed = 1.2;
    this.burst(P.smoke, PRIO.decor, 2, hand, GUNSMOKE, GUNSMOKE, 0.45, 0.4).speed = 0.25;
  }

  private alphaOf(spec: VfxSpec): number {
    return spec.hostile ? 1 : FRIENDLY_ALPHA;
  }

  // =========================================================================
  // Hits (damage / heal), impacts and ticks
  // =========================================================================

  /** +impact: a killing blow on `id` is in flight; its death waits (≤ maxS) for it to land. */
  private holdKill(id: UnitId, maxS: number): void {
    this.killHeld.add(id);
    this.onDeferDeath?.(id, maxS);
  }

  /** +impact: the held killing blow on `id` landed (or timed out): play the death now. */
  private releaseKill(id: UnitId, view: FxView): void {
    if (!this.killHeld.delete(id)) return;
    this.onReleaseDeath?.(id);
    const w = this.deathWait.get(id);
    if (w) {
      this.deathWait.delete(id);
      this.death(id, view, w.killer);
    }
  }

  private applyHit(abilityId: string, sourceId: UnitId | undefined, hit: Hit, view: FxView, parried: boolean): void {
    this.land(hit);
    if (hit.kill) this.releaseKill(hit.targetId, view);
    const isMelee = abilityId === MELEE_ABILITY_ID;
    const spec = this.spec(abilityId, sourceId, view);
    const src = sourceId ? view.units.get(sourceId) : undefined;
    if (hit.kind === 'heal') {
      if (hit.tick) this.tickOn(spec.tick === 'none' ? 'motes' : spec.tick, spec, hit.targetId, view);
      else this.impactOn(spec.heal, spec, hit.targetId, sourceId, hit.crit, view, this.prio(sourceId, view, PRIO.support));
      this.onLand?.(abilityId, spec, sourceId, hit, false, view);
      return;
    }
    if (hit.tick) {
      this.tickOn(spec.tick, spec, hit.targetId, view);
      if (spec.projectile === 'arrow' || abilityId === ARROWFALL) this.dropArrow(hit.targetId, view); // +classes: Arrowfall rains real arrows
      this.onLand?.(abilityId, spec, sourceId, hit, false, view);
      return;
    }
    // Auto-attacks: heroes get a quick swing arc; everyone gets small sparks.
    if (isMelee && src && src.kind === 'player' && !this.ownsSwing(src.id)) this.swing(src, spec, view);
    const style: ImpactStyle = parried ? 'sparks' : spec.impact;
    this.impactOn(style, spec, hit.targetId, sourceId, hit.crit, view, this.prio(sourceId, view, PRIO.impact));
    // +impact: the feel director (render/toon/feel) owns hit-stop / shake / sound when attached.
    if (this.onLand) {
      this.onLand(abilityId, spec, sourceId, hit, parried, view);
      return;
    }
    // Big crits: hit-stop and a light shake (the player's own, or anyone's while spectating).
    if (hit.crit && spec.big && sourceId) {
      const mine = sourceId === view.playerId;
      const spectating = view.playerId === null && !spec.hostile;
      if (mine || spectating) {
        this.hitStop.trigger(mine ? 70 : 45);
        this.shake.add(mine ? 0.42 : 0.22);
      }
    }
    // Heavy hits taken by the player.
    if (spec.big && spec.hostile && hit.targetId === view.playerId) this.shake.add(0.3);
  }

  private impactOn(style: ImpactStyle, spec: VfxSpec, targetId: UnitId, sourceId: UnitId | undefined, crit: boolean, view: FxView, prio: number): void {
    if (style === 'none') return;
    const u = view.units.get(targetId);
    if (!u) return;
    const h = view.heightOf(targetId);
    const p = toThree(u, h * 0.55, V1);
    const src = sourceId ? view.units.get(sourceId) : undefined;
    const dir = V2.set(0, 0, 0);
    if (src && src.id !== u.id) {
      dir.set(u.pos.x - src.pos.x, 0, -(u.pos.y - src.pos.y));
      if (dir.lengthSq() > 1e-6) dir.normalize();
    }
    this.impactAt(style, spec, p, dir, Math.max(1.0, Math.min(2.0, u.radius * 0.85 + 0.6)) * spec.scale, crit, prio, h);
  }

  private impactAt(style: ImpactStyle, spec: VfxSpec, p: THREE.Vector3, dir: THREE.Vector3, s: number, crit: boolean, prio: number, h: number): void {
    const al = this.alphaOf(spec);
    const k = crit ? 1.4 : 1;
    const col = spec.color;
    const acc = spec.accent;
    const g = V3.set(p.x, G(p.x, p.z) + 0.3, p.z);
    const dirSparks = (n: number, size: number, speed = 1): EmitReq => {
      const q = this.burst(P.sparks, prio, n, p, col, acc, size, al);
      q.dx = dir.x;
      q.dy = 0.25;
      q.dz = dir.z;
      q.speed = speed;
      return q;
    };
    switch (style) {
      case 'sparks':
        this.burst(P.flash, prio, 1, p, col, acc, 0.75 * s * k, al);
        dirSparks(crit ? 15 : 8, s);
        if (crit) this.burst(P.ring, prio, 1, p, col, acc, 0.7 * s, al);
        break;
      case 'lightning': {
        this.burst(P.flash, prio, 1, p, col, acc, 1.0 * s * k, al);
        const q = this.burst(P.sparks, prio, crit ? 20 : 12, p, col, acc, 1.1 * s, al);
        q.speed = 1.15;
        this.burst(P.boltlets, prio, crit ? 6 : 3, p, col, acc, 1.0 * s, al).radius = 0.7 * s;
        this.burst(P.ring, prio, 1, p, col, acc, 0.6 * s * k, al * 0.8);
        if (crit) {
          for (let i = 0; i < 3; i++) {
            const a = rnd() * Math.PI * 2;
            V4.set(p.x + Math.cos(a) * 1.8 * s, G(p.x, p.z) + 0.05, p.z + Math.sin(a) * 1.8 * s);
            this.bolt(p, V4, spec, 0.14, 0.11);
          }
          this.decalAdd.spawn(DECAL.ring, p.x, -p.z, 2.4 * s, col[0], col[1], col[2], 0.5 * al, 0.4);
        }
        break;
      }
      case 'fire': {
        this.burst(P.flash, prio, 1, p, col, acc, 1.1 * s * k, al);
        const fl = this.burst(P.flames, prio, crit ? 18 : 10, p, col, acc, 1.25 * s, al);
        fl.radius = 0.4 * s;
        fl.speed = 1.3;
        this.burst(P.embers, prio, crit ? 22 : 12, p, col, acc, 1.4 * s, al).radius = 0.3;
        this.burst(P.streaks, prio, crit ? 10 : 6, p, col, acc, 1.2 * s, al).speed = 0.7;
        const sm = this.burst(P.smoke, PRIO.decor, crit ? 5 : 3, p, SMOKE, SMOKE, 0.9 * s, 0.55);
        sm.radius = 0.3;
        this.burst(P.ring, prio, 1, p, col, acc, 0.8 * s * k, al * 0.8);
        this.decalAdd.spawn(DECAL.flash, p.x, -p.z, 2.2 * s * k, col[0] * 0.8, col[1] * 0.6, col[2] * 0.4, 0.35 * al, 0.45);
        if (spec.decal === 'scorch') this.decalAlpha.spawn(DECAL.scorch, p.x, -p.z, 1.5 * s * k, SCORCH[0], SCORCH[1], SCORCH[2], 0.5, 5);
        break;
      }
      case 'flameLick':
        this.burst(P.flames, prio, 7, p, col, acc, 0.85 * s, al).radius = 0.3;
        this.burst(P.embers, prio, 5, p, col, acc, 0.8 * s, al).radius = 0.2;
        this.burst(P.flash, prio, 1, p, col, acc, 0.6 * s, al);
        break;
      case 'holy':
        this.burst(P.flash, prio, 1, p, col, acc, 0.95 * s * k, al);
        this.burst(P.stars, prio, crit ? 12 : 7, p, col, acc, 1.0 * s, al).radius = 0.7;
        this.burst(P.ring, prio, 1, p, col, acc, 0.8 * s * k, al);
        this.decalAdd.spawn(DECAL.glyph, p.x, -p.z, 1.3 * s, col[0], col[1], col[2], 0.3 * al, 0.7);
        break;
      case 'holyBurst':
        this.burst(P.flash, prio, 1, p, col, acc, 1.3 * s * k, al);
        this.burst(P.stars, prio, 1, p, col, acc, 3.2 * s * k, al);
        this.burst(P.ring, prio, 1, p, col, acc, 1.0 * s * k, al);
        this.burst(P.motes, prio, crit ? 14 : 8, g, col, acc, 1.0, al).radius = 0.7;
        this.decalAdd.spawn(DECAL.ring, p.x, -p.z, 2.6 * s, col[0], col[1], col[2], 0.45 * al, 0.45);
        break;
      case 'slash':
        this.crescent(p, dir, col, acc, 1.1 * s * k, prio, al);
        dirSparks(crit ? 12 : 7, s);
        this.burst(P.flash, prio, 1, p, col, acc, 0.5 * s * k, al);
        break;
      case 'heavy':
      case 'shieldSlam':
      case 'execute':
      case 'smash': {
        const big = style === 'execute' ? 1.25 : 1;
        this.crescent(p, dir, col, acc, 1.5 * s * k * big, prio, al);
        this.burst(P.flash, prio, 1, p, col, acc, 1.1 * s * k * big, al);
        dirSparks(crit ? 22 : 14, s * big, 1.1);
        this.burst(P.ring, prio, 1, p, col, acc, 1.0 * s * k * big, al);
        this.burst(P.dust, PRIO.decor, 5, g, DUST, DUST, 0.9 * s, 0.55).radius = 0.6;
        const sl = this.burst(P.streaks, prio, crit ? 12 : 7, p, col, acc, 1.3 * s * big, al);
        sl.speed = 0.8;
        sl.radius = 0.1;
        this.decalAdd.spawn(DECAL.ring, p.x, -p.z, 2.4 * s * big, col[0], col[1], col[2], 0.5 * al, 0.4);
        if (style === 'shieldSlam') {
          this.burst(P.shards, prio, 6, p, col, acc, 0.8 * s, al);
          this.burst(P.ring, prio, 1, p, acc, acc, 1.5 * s * k, al * 0.6).life = 1.4;
        }
        if (style === 'execute' || style === 'smash') {
          this.burst(P.debris, prio, style === 'execute' ? 9 : 5, g, DEBRIS, DEBRIS, 1.0 * s, 1).radius = 0.8;
          this.burst(P.dustRing, PRIO.decor, style === 'execute' ? 10 : 6, g, DUST, DUST, 1.0 * s, 0.55).radius = 0.7;
          this.decalAlpha.spawn(DECAL.cracks, p.x, -p.z, (style === 'execute' ? 2.6 : 2.0) * s, CRACK[0], CRACK[1], CRACK[2], 0.75, 2.2);
          if (style === 'execute') this.shake.add(prio >= PRIO.player ? 0.3 : 0);
        }
        break;
      }
      case 'taunt':
        this.burst(P.ring, prio, 1, p, col, acc, 1.0 * s, al);
        this.burst(P.ring, prio, 1, p, col, acc, 1.5 * s, al * 0.6).life = 1.5;
        this.burst(P.flash, prio, 1, p, col, acc, 0.6 * s, al);
        break;
      case 'venom': {
        this.burst(P.flash, prio, 1, p, col, acc, 0.8 * s * k, al);
        const q = this.burst(P.drips, prio, crit ? 16 : 10, p, col, acc, 1.0 * s, al);
        q.radius = 0.3;
        q.speed = 1.2;
        this.burst(P.smoke, PRIO.decor, 2, p, [0.2, 0.24, 0.05], SMOKE, 0.8 * s, 0.4).radius = 0.3;
        if (crit) this.burst(P.ring, prio, 1, p, col, acc, 0.8 * s, al);
        break;
      }
      case 'shadow':
        this.burst(P.flash, prio, 1, p, col, acc, 0.9 * s * k, al);
        this.burst(P.wisps, prio, 5, p, col, acc, 0.8 * s, al).radius = 0.4;
        this.burst(P.ring, prio, 1, p, col, acc, 0.8 * s * k, al);
        dirSparks(6, s);
        break;
      case 'arcane':
        this.burst(P.flash, prio, 1, p, col, acc, 0.9 * s * k, al);
        this.burst(P.stars, prio, 8, p, col, acc, 0.9 * s, al).radius = 0.6;
        this.burst(P.ring, prio, 1, p, col, acc, 0.9 * s * k, al);
        break;
      case 'frost':
        this.burst(P.flash, prio, 1, p, col, acc, 0.9 * s * k, al);
        this.burst(P.shards, prio, crit ? 14 : 9, p, col, acc, 0.9 * s, al);
        this.decalAdd.spawn(DECAL.frost, p.x, -p.z, 1.7 * s, col[0], col[1], col[2], 0.5 * al, 2.5);
        break;
      case 'heal': {
        const r = 0.55 * s * k;
        // +impact: the heal blooms on the target (soft glow swell + a rising ring), not just a pillar.
        this.burst(P.glow, prio, 1, p, col, acc, 1.5 * s * k, al * 0.8).life = 1.6;
        this.burst(P.ring, prio, 1, g, col, acc, 0.9 * s, al * 0.7).life = 1.3;
        this.cyl.spawn(VOL.pillar, p.x, 0.02, p.z, r, Math.max(2.8, h * 1.4), 0.75, col[0], col[1], col[2], 0.55 * al, 0.6);
        this.burst(P.motes, prio, crit ? 16 : 10, g, col, acc, 1.0, al).radius = 0.65 * s;
        this.decalAdd.spawn(DECAL.ring, p.x, -p.z, 1.5 * s, col[0], col[1], col[2], 0.3 * al, 0.5);
        if (crit) this.burst(P.flash, prio, 1, p, col, acc, 0.7 * s, al);
        break;
      }
      case 'bigHeal': {
        this.burst(P.glow, prio, 1, p, col, acc, 2.2 * s * k, al * 0.85).life = 2;
        const up = this.burst(P.sparks, prio, crit ? 14 : 9, p, col, acc, 0.8, al);
        up.dy = 1;
        up.speed = 0.55;
        this.cyl.spawn(VOL.pillar, p.x, 0.02, p.z, 0.95 * s, Math.max(4.5, h * 2.2), 0.6, col[0], col[1], col[2], 0.6 * al, 0.9);
        this.burst(P.motes, prio, crit ? 28 : 20, g, col, acc, 1.1, al).radius = 0.9 * s;
        this.decalAdd.spawn(DECAL.glyph, p.x, -p.z, 2.3 * s, acc[0], acc[1], acc[2], 0.4 * al, 1.4);
        this.burst(P.ring, prio, 1, p, col, acc, 1.2 * s, al);
        this.burst(P.flash, prio, 1, p, col, acc, 0.9 * s, al);
        break;
      }
      case 'motes':
        this.burst(P.motes, prio, 6, g, col, acc, 0.9, al).radius = 0.5;
        this.burst(P.flash, prio, 1, p, col, acc, 0.45 * s, al * 0.6);
        break;
      case 'cleanse': {
        const q = this.burst(P.sparks, prio, 10, p, col, acc, 0.9, al);
        q.dy = 1;
        q.speed = 0.6;
        this.burst(P.ring, prio, 1, p, col, acc, 0.8 * s, al);
        this.burst(P.motes, prio, 6, g, col, acc, 0.9, al).radius = 0.5;
        break;
      }
      case 'none':
        break;
    }
    // +impact: crit "impact frame": a 2–3 frame hot core, radial streaks and a crisp ring. Short,
    // so it punches without washing the screen (moderate peak, friendly alpha).
    if (crit && !spec.hostile && style !== 'heal' && style !== 'bigHeal' && style !== 'motes' && style !== 'cleanse') {
      const mine = prio >= PRIO.player;
      const f = this.burst(P.flash, prio, 1, p, acc, acc, (mine ? 2.1 : 1.5) * s, al * 0.9);
      f.life = 0.38;
      const st = this.burst(P.streaks, prio, mine ? 10 : 6, p, col, acc, 1.25 * s, al);
      st.speed = 1.5;
      st.radius = 0.05;
      const r = this.burst(P.ring, prio, 1, p, acc, acc, 1.3 * s, al * 0.8);
      r.life = 0.6;
    }
  }

  private tickOn(style: TickStyle, spec: VfxSpec, targetId: UnitId, view: FxView): void {
    if (style === 'none') return;
    const u = view.units.get(targetId);
    if (!u) return;
    const p = toThree(u, view.heightOf(targetId) * 0.5, V1);
    const g = V3.set(p.x, G(p.x, p.z) + 0.3, p.z);
    const al = this.alphaOf(spec);
    const col = spec.color;
    const acc = spec.accent;
    const pr = PRIO.loop;
    switch (style) {
      case 'flameLick':
        this.burst(P.flames, pr, 4, p, col, acc, 0.75, al).radius = 0.3;
        this.burst(P.embers, pr, 3, p, col, acc, 0.7, al).radius = 0.2;
        break;
      case 'quake':
        this.burst(P.debris, pr, 2, g, DEBRIS, DEBRIS, 0.8, 1).radius = 0.5;
        this.burst(P.dust, PRIO.decor, 2, g, DUST, DUST, 0.7, 0.5).radius = 0.4;
        break;
      case 'holy':
        this.burst(P.stars, pr, 3, p, col, acc, 0.7, al).radius = 0.5;
        break;
      case 'venom':
        this.burst(P.drips, pr, 4, p, col, acc, 0.8, al).radius = 0.3;
        break;
      case 'motes':
        this.burst(P.motes, pr, 2, g, col, acc, 0.8, al).radius = 0.4;
        break;
      case 'bleed':
        this.burst(P.drips, pr, 4, p, C.hostilePhys, [0.7, 0.1, 0.08], 0.8, 0.9).radius = 0.25;
        break;
      case 'blade':
        this.crescent(p, ZERO, col, acc, 0.9, pr, al);
        this.burst(P.sparks, pr, 4, p, col, acc, 0.8, al);
        break;
      case 'school':
        this.burst(P.sparks, pr, 3, p, col, acc, 0.7, al).speed = 0.6;
        this.burst(P.flash, pr, 1, p, col, acc, 0.4, al * 0.6);
        break;
    }
  }

  // =========================================================================
  // Ability clip cues (render/toon/anim/clips.ts)
  // =========================================================================

  /**
   * A clip's fx marker: a small accent at `p` (world), facing `dir` (unit forward, horizontal),
   * tinted with the clip colour. `mine`: the local player's clip (higher budget priority).
   */
  clipCue(kind: FxCueId, p: THREE.Vector3, dir: THREE.Vector3, color: RGB, scale: number, mine: boolean): void {
    if (this.k <= 0.01) return;
    const prio = mine ? PRIO.player : PRIO.impact;
    const al = FRIENDLY_ALPHA;
    const acc = CUE_ACC;
    acc[0] = color[0] + (1 - color[0]) * 0.55;
    acc[1] = color[1] + (1 - color[1]) * 0.55;
    acc[2] = color[2] + (1 - color[2]) * 0.55;
    const s = Math.max(0.2, scale);
    const g = V3.set(p.x, G(p.x, p.z) + 0.25, p.z);
    switch (kind) {
      case 'impact': {
        this.burst(P.flash, prio, 1, p, color, acc, 0.6 * s, al);
        this.crescent(p, dir, color, acc, 1.0 * s, prio, al);
        const q = this.burst(P.sparks, prio, Math.round(8 * s), p, color, acc, 0.8 * s, al);
        q.dx = dir.x;
        q.dy = 0.25;
        q.dz = dir.z;
        break;
      }
      case 'sparks': {
        const q = this.burst(P.sparks, prio, Math.max(2, Math.round(7 * s)), p, color, acc, 0.75 * s, al);
        q.dx = dir.x;
        q.dy = 0.3;
        q.dz = dir.z;
        q.speed = 0.8;
        break;
      }
      case 'slash':
        this.crescent(p, dir, color, acc, 1.2 * s, prio, al);
        this.burst(P.flash, prio, 1, p, color, acc, 0.4 * s, al);
        break;
      case 'shockwave':
        this.decalAdd.spawn(DECAL.ring, p.x, -p.z, 2.4 * s, color[0], color[1], color[2], 0.5 * al, 0.4);
        this.burst(P.ring, prio, 1, g, color, acc, 1.0 * s, al);
        this.burst(P.dustRing, PRIO.decor, Math.round(8 * s), g, DUST, DUST, 0.9 * s, 0.5).radius = 0.8 * s;
        break;
      case 'flash':
        this.burst(P.flash, prio, 1, p, color, acc, 0.7 * s, al);
        break;
      case 'dust':
        this.burst(P.dust, PRIO.decor, Math.max(2, Math.round(5 * s)), g, DUST, DUST, 0.8 * s, 0.5).radius = 0.6 * s;
        break;
      case 'holy':
        this.burst(P.stars, prio, Math.round(6 * s), p, color, acc, 0.8 * s, al).radius = 0.5 * s;
        this.burst(P.ring, prio, 1, p, color, acc, 0.6 * s, al);
        break;
      case 'electric':
        this.burst(P.boltlets, prio, Math.max(2, Math.round(4 * s)), p, color, acc, 0.9 * s, al).radius = 0.5 * s;
        this.burst(P.sparks, prio, Math.round(6 * s), p, color, acc, 0.7 * s, al).speed = 1.1;
        break;
      case 'embers':
        this.burst(P.embers, prio, Math.round(8 * s), p, color, acc, 0.9 * s, al).radius = 0.3 * s;
        break;
      case 'cracks':
        this.decalAlpha.spawn(DECAL.cracks, p.x, -p.z, 1.8 * s, CRACK[0], CRACK[1], CRACK[2], 0.7, 1.6);
        this.burst(P.dust, PRIO.decor, Math.round(5 * s), g, DUST, DUST, 0.9 * s, 0.5).radius = 0.7 * s;
        break;
    }
  }

  // =========================================================================
  // Primitive helpers
  // =========================================================================

  private burst(preset: Preset, prio: number, count: number, p: THREE.Vector3, color: RGB, accent: RGB, size: number, alpha: number): EmitReq {
    const q = this.emit.req(preset, prio, count, p.x, p.y, p.z);
    tint(q, color, accent, alpha);
    q.size = size;
    q.clearFloor = alpha < 1;
    return q;
  }

  private crescent(p: THREE.Vector3, dir: THREE.Vector3, col: RGB, acc: RGB, size: number, prio: number, al: number): void {
    const q = this.burst(P.crescent, prio, 1, p, col, acc, size, al);
    // Screen-space rotation: roughly perpendicular to the hit direction, with variation.
    q.rot = dir.lengthSq() > 0 ? Math.atan2(dir.z, dir.x) + Math.PI / 2 : rnd() * Math.PI * 2;
  }

  private schedule(delayS: number, run: () => void): void {
    this.scheduled.push({ at: this.now + delayS, run });
  }

  private bolt(a: THREE.Vector3, b: THREE.Vector3, spec: VfxSpec, life: number, width: number): void {
    if (this.bolts.length >= MAX_BOLTS) return;
    const strip = this.arcs.alloc(spec.color[0], spec.color[1], spec.color[2], 0.95, 0);
    if (strip < 0) return;
    const len = a.distanceTo(b);
    const branch = len > 3 ? this.arcs.alloc(spec.color[0], spec.color[1], spec.color[2], 0.8, 0) : -1;
    this.bolts.push({ alpha: this.alphaOf(spec), strip, branch, a: a.clone(), b: b.clone(), age: 0, life, jitter: 0, width });
  }

  /**
   * +classes: arrows falling onto a unit from above (Arrowfall ticks). Visual only: no hits.
   * +volley: a tick drops a small staggered volley (not one lone arrow) so the rain reads at a glance.
   */
  private dropArrow(targetId: UnitId, view: FxView): void {
    const tgt = view.units.get(targetId);
    if (!tgt) return;
    const spec = this.spec(ARROWFALL, undefined, view);
    const n = abilityVolley(this.projectiles.length);
    for (let k = 0; k < n; k++) {
      const from = toThree(tgt, 6.5 + k * 1.3, V4).clone();
      from.x += (rnd() - 0.5) * 2.6;
      from.z += (rnd() - 0.5) * 2.6 - 1.2;
      this.projectiles.push({ spec: { ...spec, projectile: 'arrow', impact: 'none' }, abilityId: ARROWFALL, sourceId: targetId, targetId, from, last: from.clone(), t: 0, dur: 0.2 + k * 0.07, arc: 0, hits: [], strip: -1, hist: new Float32Array(TRAIL_POINTS * 3), histN: 0, histHead: 0, arrived: false, fade: 0, acc: 0, prio: PRIO.loop });
    }
  }

  private launch(spec: VfxSpec, abilityId: string, sourceId: UnitId, targetId: UnitId, hits: Hit[], view: FxView): void {
    const src = view.units.get(sourceId);
    const tgt = view.units.get(targetId);
    if (!src || !tgt) return;
    const from = (view.handOf(sourceId, V4) ?? toThree(src, view.heightOf(sourceId) * 0.62, V4)).clone();
    const dist = Math.hypot(tgt.pos.x - src.pos.x, tgt.pos.y - src.pos.y);
    const dur = spec.projectile === 'bullet' ? Math.max(0.05, dist / (spec.speed ?? 140)) : Math.max(0.12, Math.min(0.55, dist / (spec.speed ?? 34))); // +ranger-specs: bullets are near-instant
    const strip = this.trails.alloc(spec.color[0], spec.color[1], spec.color[2], spec.projectile === 'wind' ? 0.4 : 0.7, 0);
    this.projectiles.push({
      spec,
      abilityId,
      sourceId,
      targetId,
      from,
      last: from.clone(),
      t: 0,
      dur,
      arc: spec.projectile === 'wind' ? 0.15 : spec.projectile === 'bullet' ? 0 : spec.projectile === 'arrow' ? Math.min(0.9, dist * 0.03) : Math.min(1.4, dist * 0.06), // +ranger-specs: bullets fly flat
      hits,
      strip,
      hist: new Float32Array(TRAIL_POINTS * 3),
      histN: 0,
      histHead: 0,
      arrived: false,
      fade: 0,
      acc: 0,
      prio: this.prio(sourceId, view, spec.hostile ? PRIO.impact : PRIO.support + 1),
    });
  }

  private chain(spec: VfxSpec, abilityId: string, sourceId: UnitId, primary: UnitId, hits: Hit[], view: FxView): void {
    const src = view.units.get(sourceId);
    if (!src) return;
    // Primary first, then the rest in event order.
    const order: Hit[] = [];
    const first = hits.find((h) => h.targetId === primary);
    if (first) order.push(first);
    for (const h of hits) if (h !== first) order.push(h);
    if (order.length === 0) {
      const t = view.units.get(primary);
      if (t) order.push({ kind: 'damage', targetId: primary, crit: false, tick: false, amount: 0, kill: false, aoe: false });
    }
    const from = (view.handOf(sourceId, V4) ?? toThree(src, view.heightOf(sourceId) * 0.6, V4)).clone();
    let prevId: UnitId | null = null;
    order.forEach((h, i) => {
      const prev = prevId;
      this.schedule(i * 0.085, () => {
        const t = view.units.get(h.targetId);
        if (!t) return;
        const a = prev ? view.units.get(prev) : undefined;
        if (a) toThree(a, view.heightOf(a.id) * 0.55, V1);
        else V1.copy(from);
        toThree(t, view.heightOf(t.id) * 0.55, V2);
        // +impact: chain arcs linger a beat (bright snap, slow afterglow) so the jumps read.
        this.bolt(V1, V2, spec, 0.42, i === 0 ? 0.3 : 0.26);
        if (h.amount > 0 || i === 0) this.applyHit(abilityId, sourceId, h, view, false);
        else this.land(h);
      });
      prevId = h.targetId;
    });
  }

  private swing(src: RenderUnit, spec: VfxSpec, view: FxView): void {
    const strip = this.arcs.alloc(spec.color[0], spec.color[1], spec.color[2], 0.7, 0);
    if (strip < 0) return;
    const h = view.heightOf(src.id);
    const f = new THREE.Vector3(Math.cos(src.facing), 0, -Math.sin(src.facing));
    const r = new THREE.Vector3(Math.sin(src.facing), 0, Math.cos(src.facing));
    const rad = Math.max(1.1, src.radius * 0.9 + 0.7) * (spec.swing === 'wide' ? 1.25 : 1) * Math.min(1.4, spec.scale);
    this.swings.push({
      strip,
      c: toThree(src, h * 0.55, new THREE.Vector3()),
      f,
      r,
      style: spec.swing ?? 'slash',
      rad,
      age: 0,
      life: spec.swing === 'thrust' ? 0.16 : spec.swing === 'overhead' ? 0.26 : 0.22,
      width: 0.42 * Math.min(1.4, spec.scale),
    });
  }

  private beam(a: THREE.Vector3, b: THREE.Vector3, spec: VfxSpec, sourceId: UnitId | null, targetId: UnitId | null, life: number, width: number, dash: boolean): void {
    const strip = this.trails.alloc(spec.color[0], spec.color[1], spec.color[2], dash ? 0.3 : 0.6, dash ? 0 : 14);
    if (strip < 0) return;
    this.beams.push({ strip, a: a.clone(), b: b.clone(), sourceId, targetId, age: 0, life, width, dash });
  }

  private nova(src: RenderUnit, spec: VfxSpec, view: FxView, prio: number): void {
    const al = this.alphaOf(spec);
    const col = spec.color;
    const acc = spec.accent;
    const s = spec.scale;
    const h = view.heightOf(src.id);
    const p = toThree(src, h * 0.5, V1);
    const R = (spec.hostile ? 8 : 5.5) * s;
    this.decalAdd.spawn(DECAL.ring, p.x, -p.z, R, col[0], col[1], col[2], 0.55 * al, 0.55);
    this.decalAdd.spawn(DECAL.flash, p.x, -p.z, R * 0.5, col[0], col[1], col[2], 0.3 * al, 0.4);
    this.burst(P.flash, prio, 1, p, col, acc, 1.2 * s, al);
    this.burst(P.ring, prio, 1, p, col, acc, 1.6 * s, al);
    const q = this.burst(P.sparks, prio, 16, V3.set(p.x, G(p.x, p.z) + 0.6, p.z), col, acc, 1.0, al);
    q.dy = 0.05;
    if (spec.zone === 'consecrate') this.burst(P.stars, prio, 12, p, col, acc, 0.9, al).radius = 2.4;
    if (spec.key.includes('stormkeeper')) this.burst(P.boltlets, prio, 8, p, col, acc, 1.1, al).radius = 1.1;
    if (spec.hostile) this.burst(P.dustRing, PRIO.decor, 10, V3.set(p.x, G(p.x, p.z) + 0.3, p.z), DUST, DUST, 1.0, 0.5).radius = 1.2;
  }

  private wave(src: RenderUnit, spec: VfxSpec, abilityId: string, hits: Hit[], view: FxView): void {
    const al = this.alphaOf(spec);
    const col = spec.color;
    const acc = spec.accent;
    const p = toThree(src, view.heightOf(src.id) * 0.5, V1);
    const R = 40;
    const speed = 45;
    this.decalAdd.spawn(DECAL.ring, p.x, -p.z, R, col[0], col[1], col[2], 0.4 * al, R / speed + 0.35);
    this.decalAdd.spawn(DECAL.glyph, p.x, -p.z, 3.2, acc[0], acc[1], acc[2], 0.4 * al, 1.2);
    this.burst(P.flash, PRIO.support, 1, p, col, acc, 1.6, al);
    this.burst(P.ring, PRIO.support, 1, p, col, acc, 2.0, al);
    this.burst(P.motes, PRIO.support, 18, V3.set(p.x, G(p.x, p.z) + 0.3, p.z), col, acc, 1.0, al).radius = 1.4;
    const ox = src.pos.x;
    const oy = src.pos.y;
    // Heals land as the ring reaches each ally.
    this.schedule(0, () => {
      for (const h of hits) {
        const t = view.units.get(h.targetId);
        const d = t ? Math.hypot(t.pos.x - ox, t.pos.y - oy) : 0;
        this.schedule(d / speed, () => this.applyHit(abilityId, src.id, h, view, false));
      }
    });
  }

  private shatter(targetId: UnitId, sourceId: UnitId | undefined, view: FxView): void {
    const u = view.units.get(targetId);
    if (!u) return;
    const p = toThree(u, view.heightOf(targetId) * 0.6, V1);
    const pr = PRIO.danger;
    this.burst(P.flash, pr, 1, p, C.interrupt, C.interruptCore, 1.2, 0.9);
    this.burst(P.shards, pr, 18, p, C.interrupt, C.interruptCore, 1.5, 1);
    this.burst(P.streaks, pr, 8, p, C.interrupt, C.interruptCore, 1.2, 0.9).speed = 0.8;
    this.burst(P.ring, pr, 1, p, C.interrupt, C.interruptCore, 1.3, 0.9);
    this.burst(P.stars, pr, 4, p, C.interrupt, C.interruptCore, 0.8, 0.9).radius = 0.5;
    this.decalAdd.spawn(DECAL.ring, p.x, -p.z, 2.2, C.interrupt[0], C.interrupt[1], C.interrupt[2], 0.6, 0.35);
    if (sourceId && sourceId === view.playerId) this.shake.add(0.18);
  }

  private death(targetId: UnitId, view: FxView, killerId?: UnitId): void {
    this.endCast(targetId);
    for (const a of this.auras) if (a.unitId === targetId) this.endAura(a.key);
    const u = view.units.get(targetId);
    if (!u) return;
    const h = view.heightOf(targetId);
    const friendly = u.faction === 'party';
    const soul: RGB = friendly ? [0.55, 0.62, 0.75] : C.soul;
    const p = toThree(u, h * 0.4, V1);
    const sm = this.burst(P.smoke, PRIO.impact, 8, p, SMOKE, SMOKE, 1.0 + u.radius * 0.3, 0.7);
    sm.radius = 0.4 + u.radius * 0.3;
    sm.life = 1.2;
    this.burst(P.wisps, PRIO.impact, 6, p, soul, soul, 0.9, 0.7).radius = 0.5;
    const q = this.burst(P.motes, PRIO.impact, 8, V3.set(p.x, G(p.x, p.z) + 0.4, p.z), soul, [0.85, 0.85, 1.0], 0.8, 0.7);
    q.radius = 0.6 + u.radius * 0.3;
    q.speed = 1.3;
    this.decalAdd.spawn(DECAL.ring, p.x, -p.z, 1.8 + u.radius, soul[0], soul[1], soul[2], 0.35, 0.7);
    if (friendly) return;
    // +impact: enemies pop when they die (a quick soul flash and spark burst) and thud when the
    // body lands (dust ring at the feet, timed to the fall in anim.ts). The player's kills pop bigger.
    const mine = !!killerId && killerId === view.playerId;
    const pr = mine ? PRIO.player : PRIO.impact;
    const big = (mine ? 1.25 : 1) * (0.9 + u.radius * 0.25);
    const al = FRIENDLY_ALPHA;
    toThree(u, h * 0.55, V2);
    this.burst(P.flash, pr, 1, V2, soul, [0.92, 0.9, 1.0], 1.4 * big, al);
    const sp = this.burst(P.sparks, pr, mine ? 16 : 10, V2, soul, [0.9, 0.88, 1.0], 1.0 * big, al);
    sp.dy = 0.6;
    sp.speed = 1.1;
    this.burst(P.ring, pr, 1, V2, soul, [0.9, 0.88, 1.0], 1.2 * big, al * 0.8);
    const fx = u.pos.x;
    const fy = u.pos.y;
    const rad = u.radius;
    this.schedule(0.6, () => {
      V3.set(fx, groundY(fx, fy) + 0.3, -fy);
      this.burst(P.dustRing, PRIO.decor, 8, V3, DUST, DUST, 0.9 + rad * 0.3, 0.5).radius = 0.5 + rad * 0.4;
      this.burst(P.dust, PRIO.decor, 4, V3, DUST, DUST, 0.8, 0.45).radius = 0.6;
    });
  }

  /** +impact: a proc lit up on `unitId`: a small golden ding of stars and a ring around the hero. */
  procSpark(unitId: UnitId, view: FxView, color: RGB): void {
    if (this.k <= 0.01) return;
    const u = view.units.get(unitId);
    if (!u) return;
    const p = toThree(u, view.heightOf(unitId) * 0.6, V1);
    const acc = CUE_ACC;
    acc[0] = color[0] + (1 - color[0]) * 0.6;
    acc[1] = color[1] + (1 - color[1]) * 0.6;
    acc[2] = color[2] + (1 - color[2]) * 0.6;
    this.burst(P.stars, PRIO.player, 8, p, color, acc, 0.8, FRIENDLY_ALPHA).radius = 0.7;
    this.burst(P.ring, PRIO.player, 1, p, color, acc, 0.9, FRIENDLY_ALPHA * 0.8).life = 1.2;
    this.burst(P.flash, PRIO.player, 1, p, color, acc, 0.8, FRIENDLY_ALPHA * 0.8);
  }

  private slam(area: WorldShape, spec: VfxSpec, view: FxView): void {
    // Lean on the telegraph's warm orange so the slam reads as "the danger zone went off".
    const col = mixRGB(C.hostilePhys, spec.color, 0.35, SLAM_COL);
    const acc = mixRGB(C.hostilePhysCore, spec.accent, 0.3, SLAM_ACC);
    const pr = PRIO.danger;
    const A = shapeArea(area);
    this.decalAdd.spawn(DECAL.slam, area.origin.x, area.origin.y, Math.sqrt(A / Math.PI), col[0], col[1], col[2], 0.75, 0.55, area);
    const n = Math.max(3, Math.min(14, Math.round(A / 8)));
    for (let i = 0; i < n; i++) {
      samplePointInShape(area, rnd(), rnd(), P2);
      V4.set(P2.x, groundY(P2.x, P2.y) + 0.3, -P2.y);
      this.burst(P.dust, PRIO.decor, 2, V4, DUST, DUST, 1.0, 0.55).radius = 0.6;
      if (i % 2 === 0) this.burst(P.debris, PRIO.impact, 3, V4, DEBRIS, DEBRIS, 1.4, 1).radius = 0.6;
      if (i % 3 === 0) {
        const q = this.burst(P.sparks, pr, 4, V4, col, acc, 1.0, 1);
        q.dy = 1;
        q.speed = 0.7;
      }
    }
    shapeCenter(area, P2);
    V4.set(P2.x, groundY(P2.x, P2.y) + 0.5, -P2.y);
    this.burst(P.flash, pr, 1, V4, col, acc, Math.min(3, 1 + Math.sqrt(A) * 0.15), 0.8);
    if (area.shape.type === 'circle' || area.shape.type === 'ring') {
      const R = area.shape.type === 'circle' ? area.shape.radius : area.shape.outer;
      this.decalAdd.spawn(DECAL.ring, area.origin.x, area.origin.y, R * 1.15, acc[0], acc[1], acc[2], 0.6, 0.45);
      this.burst(P.dustRing, PRIO.decor, Math.min(18, 6 + R), V4.set(area.origin.x, groundY(area.origin.x, area.origin.y) + 0.3, -area.origin.y), DUST, DUST, 1.1, 0.55).radius = R * 0.9;
    }
    const d = Math.hypot(P2.x - view.focus.x, P2.y - view.focus.y);
    this.shake.add(0.32 * clamp01(1 - d / 28) * Math.max(0.4, Math.min(1.1, Math.sqrt(A) / 8)));
  }

  // =========================================================================
  // Cast charge-up loops
  // =========================================================================

  private startCast(unitId: UnitId, spec: VfxSpec, durS: number): void {
    this.endCast(unitId);
    this.casts.push({ unitId, spec, until: this.now + Math.max(0.2, durS) + 0.4, acc: 0, pulse: 0, dead: false });
  }

  private endCast(unitId: UnitId): void {
    for (const c of this.casts) if (c.unitId === unitId) c.dead = true;
  }

  private updateCasts(dt: number, view: FxView): void {
    let w = 0;
    for (let i = 0; i < this.casts.length; i++) {
      const c = this.casts[i]!;
      const u = view.units.get(c.unitId);
      if (c.dead || !u || !u.alive || this.now > c.until || u.casting === false) continue;
      this.casts[w++] = c;
      const hand = view.handOf(c.unitId, V1);
      if (!hand) continue; // off-screen
      const sp = c.spec;
      const al = this.alphaOf(sp);
      const pr = sp.hostile ? PRIO.impact : PRIO.loop;
      const prog = u.cast?.progress ?? 0.5;
      c.acc += dt * (14 + 26 * prog);
      const n = Math.floor(c.acc);
      c.acc -= n;
      if (n > 0) {
        const q = this.burst(P.converge, pr, n, hand, sp.color, sp.accent, 1.0, al);
        q.radius = 0.9 + 0.3 * u.radius;
      }
      c.pulse -= dt;
      if (c.pulse <= 0) {
        c.pulse = sp.hostile ? 0.45 : 0.12;
        switch (sp.cast) {
          case 'storm':
            this.burst(P.boltlets, pr, 2, hand, sp.color, sp.accent, 0.7, al).radius = 0.35;
            if (rnd() < 0.5) {
              V2.set(hand.x + (rnd() - 0.5) * 1.2, hand.y + (rnd() - 0.3) * 0.9, hand.z + (rnd() - 0.5) * 1.2);
              this.bolt(hand, V2, sp, 0.08, 0.07);
            }
            break;
          case 'flame':
            this.burst(P.flames, pr, 2, hand, sp.color, sp.accent, 0.5, al).radius = 0.12;
            break;
          case 'holy':
            this.burst(P.stars, pr, 1, hand, sp.color, sp.accent, 0.6, al).radius = 0.4;
            break;
          default:
            this.burst(P.glow, pr, 1, hand, sp.color, sp.accent, 0.5 + 0.4 * prog, al * 0.8);
        }
        if (sp.hostile) {
          // Enemy casts: a warm pulse at the feet so the cast reads from across the room.
          const R = 1.2 + u.radius * 0.8;
          this.decalAdd.spawn(DECAL.ring, u.pos.x, u.pos.y, R, sp.color[0], sp.color[1], sp.color[2], 0.45 + 0.25 * prog, 0.55);
        }
      }
    }
    this.casts.length = w;
  }

  // =========================================================================
  // Aura loops
  // =========================================================================

  private startAura(unitId: UnitId, auraId: string, sourceId: UnitId | undefined, view: FxView): void {
    const vfx = resolveAuraVfx(auraId);
    if (!vfx) return;
    const key = `${unitId}|${auraId}`;
    if (this.auraIndex.has(key)) return;
    const u = view.units.get(unitId);
    if (!u) return;
    const def = this.content?.aura(auraId);
    const until = def?.durationMs ? this.now + def.durationMs / 1000 + 1.5 : Infinity;
    const h = view.heightOf(unitId);
    const loop: AuraLoop = { key, unitId, vfx, hostileSrc: this.hostile(sourceId, view), vol: -1, vol2: -1, decal: -1, acc: 0, acc2: 0, until, dead: false };
    const c = vfx.color;
    const a = vfx.accent;
    const al = loop.hostileSrc ? 0.9 : FRIENDLY_ALPHA;
    const R = Math.max(0.9, u.radius * 1.15 + 0.35);
    const x = u.pos.x;
    const z = -u.pos.y;
    const gh = groundY(u.pos.x, u.pos.y);
    switch (vfx.style) {
      case 'stormkeeper':
        loop.vol = this.cyl.spawn(VOL.aura, x, 0.1, z, R * 0.95, h * 1.05, 0.75, c[0], c[1], c[2], 0.7 * al, 0.6, 1.2, 0.5);
        this.burst(P.boltlets, PRIO.support, 8, V1.set(x, gh + h * 0.6, z), c, a, 1.0, al).radius = 1;
        break;
      case 'bubble':
      case 'aegis':
      case 'astral':
      case 'ward':
        loop.vol = this.sph.spawn(VOL.bubble, x, h * 0.5, z, R * 1.2, Math.max(R * 1.2, h * 0.62), 1, c[0], c[1], c[2], (vfx.style === 'bubble' ? 0.6 : 0.45) * al, 0.8, 0.3, 0.5);
        this.burst(P.ring, PRIO.support, 1, V1.set(x, gh + h * 0.5, z), c, a, 1.4, al);
        break;
      case 'divine':
        loop.vol = this.sph.spawn(VOL.divine, x, h * 0.5, z, R * 1.25, Math.max(R * 1.25, h * 0.65), 1, c[0], c[1], c[2], 0.6 * al, 0.8, 0.3, 0.5);
        loop.decal = this.decalAdd.spawn(DECAL.glyph, x, u.pos.y, R * 1.4, a[0], a[1], a[2], 0.35 * al, 1.0, undefined, 0.5);
        this.burst(P.flash, PRIO.support, 1, V1.set(x, gh + h * 0.5, z), c, a, 1.6, al);
        break;
      case 'shieldWall':
        loop.vol = this.sph.spawn(VOL.bubble, x, h * 0.5, z, R * 1.1, Math.max(R * 1.1, h * 0.58), 1, c[0], c[1], c[2], 0.3 * al, 0.8, -0.4, 0.5);
        break;
      case 'bladestorm':
        loop.vol = this.cyl.spawn(VOL.vortex, x, 0.15, z, R * 1.6, h * 0.85, 1.45, c[0], c[1], c[2], 0.5 * al, 0.6, 9, 0.5);
        loop.vol2 = this.cyl.spawn(VOL.vortex, x, 0.3, z, R * 1.2, h * 0.55, 1.6, a[0], a[1], a[2], 0.32 * al, 0.6, 13, 0.5);
        break;
      case 'parry':
        loop.decal = this.decalAdd.spawn(DECAL.ring, x, u.pos.y, R * 1.2, c[0], c[1], c[2], 0.3 * al, 1.0, undefined, 0.85);
        break;
      case 'smash':
        loop.decal = this.decalAlpha.spawn(DECAL.cracks, x, u.pos.y, R * 1.3, 0.2, 0.03, 0.02, 0.35, 1.0, undefined, 0.5);
        break;
      case 'ritual':
        loop.vol = this.cyl.spawn(VOL.aura, x, 0.1, z, R, h * 1.1, 0.8, c[0], c[1], c[2], 0.55, 0.6, 1.0, 0.5);
        break;
      default:
        break;
    }
    this.auras.push(loop);
    this.auraIndex.set(key, loop);
  }

  private endAura(key: string): void {
    const loop = this.auraIndex.get(key);
    if (!loop) return;
    loop.dead = true;
    this.auraIndex.delete(key);
  }

  private updateAuras(dt: number, view: FxView): void {
    let w = 0;
    for (let i = 0; i < this.auras.length; i++) {
      const loop = this.auras[i]!;
      const u = view.units.get(loop.unitId);
      if (loop.dead || !u || !u.alive || this.now > loop.until) {
        if (!loop.dead) this.auraIndex.delete(loop.key);
        this.releaseAuraFor(loop);
        continue;
      }
      this.auras[w++] = loop;
      const h = view.heightOf(u.id);
      const x = u.pos.x;
      const z = -u.pos.y;
      const gh = groundY(u.pos.x, u.pos.y);
      const v = loop.vfx;
      const c = v.color;
      const a = v.accent;
      const al = loop.hostileSrc ? 0.9 : FRIENDLY_ALPHA;
      const R = Math.max(0.9, u.radius * 1.15 + 0.35);
      const pr = PRIO.loop;
      const isSphere = v.style === 'bubble' || v.style === 'aegis' || v.style === 'astral' || v.style === 'ward' || v.style === 'divine' || v.style === 'shieldWall';
      if (isSphere) this.sph.move(loop.vol, x, h * 0.5, z);
      else {
        this.cyl.move(loop.vol, x, v.style === 'bladestorm' ? 0.15 : 0.1, z);
        this.cyl.move(loop.vol2, x, 0.3, z);
      }
      loop.acc += dt;
      const every = (s: number): boolean => {
        if (loop.acc < s) return false;
        loop.acc -= s;
        if (loop.acc > s) loop.acc = 0;
        return true;
      };
      V1.set(x, gh + h * 0.55, z);
      switch (v.style) {
        case 'stormkeeper':
          if (every(0.1)) {
            this.burst(P.boltlets, pr, 1, V1, c, a, 0.8, al).radius = R * 0.9;
            loop.acc2 += 1;
            if (loop.acc2 % 4 === 0) {
              V2.set(x + (rnd() - 0.5) * R * 1.6, h * (0.2 + rnd() * 0.8), z + (rnd() - 0.5) * R * 1.6);
              V3.set(x + (rnd() - 0.5) * R * 1.6, h * (0.2 + rnd() * 0.8), z + (rnd() - 0.5) * R * 1.6);
              this.bolt(V2, V3, STORM_LOOP_SPEC, 0.1, 0.06);
            }
          }
          break;
        case 'bladestorm':
          if (every(0.05)) {
            const ang = this.now * 14;
            V2.set(x + Math.cos(ang) * R * 1.4, h * 0.5, z + Math.sin(ang) * R * 1.4);
            const q = this.burst(P.streaks, pr, 2, V2, c, a, 1.0, al);
            q.dx = -Math.sin(ang);
            q.dz = Math.cos(ang);
            q.speed = 0.7;
            if (rnd() < 0.35) this.burst(P.crescent, pr, 1, V1, c, a, 1.6, al * 0.7).rot = ang;
            this.burst(P.dust, PRIO.decor, 1, V2.set(x, gh + 0.3, z), DUST, DUST, 0.8, 0.4).radius = R * 1.4;
          }
          break;
        case 'parry':
          if (every(0.4)) this.burst(P.crescent, pr, 1, V1, c, a, 1.1, al * 0.6).rot = this.now * 3;
          break;
        case 'burning':
          if (every(0.13)) {
            this.burst(P.flames, pr, 1, V1.set(x, gh + h * (0.3 + rnd() * 0.5), z), c, a, 0.55, al).radius = R * 0.45;
            if (rnd() < 0.5) this.burst(P.embers, pr, 1, V1, c, a, 0.6, al).radius = R * 0.4;
          }
          break;
        case 'motes':
          if (every(0.35)) this.burst(P.motes, pr, 1, V2.set(x, gh + 0.3, z), c, a, 0.7, al).radius = R * 0.7;
          break;
        case 'freedom':
          if (every(0.15)) {
            const q = this.burst(P.motes, pr, 1, V2.set(x, gh + 0.2, z), c, a, 0.8, al);
            q.radius = R;
            q.speed = 1.4;
          }
          break;
        case 'divine':
        case 'aegis':
          if (every(0.25)) this.burst(P.stars, pr, 1, V1, c, a, 0.6, al).radius = R;
          break;
        case 'astral':
          if (every(0.3)) this.burst(P.wisps, pr, 1, V1, c, a, 0.6, al * 0.7).radius = R * 0.6;
          break;
        case 'hypnosis':
          if (every(0.12)) {
            const ang = this.now * 5;
            V2.set(x + Math.cos(ang) * 0.5, h + 0.35, z + Math.sin(ang) * 0.5);
            this.burst(P.stars, pr, 1, V2, c, a, 0.5, 0.9).radius = 0.05;
          }
          break;
        case 'petrified':
          if (every(0.3)) this.burst(P.dust, pr, 1, V1, c, c, 0.6, 0.5).radius = R * 0.5;
          break;
        case 'venom':
          if (every(0.45)) this.burst(P.drips, pr, 2, V1, c, a, 0.7, 0.9).radius = R * 0.4;
          break;
        case 'bleed':
          if (every(0.5)) this.burst(P.drips, pr, 2, V1, c, [0.7, 0.1, 0.08], 0.7, 0.9).radius = R * 0.3;
          break;
        default:
          break;
      }
    }
    this.auras.length = w;
  }

  private releaseAuraFor(loop: AuraLoop): void {
    const v = loop.vfx.style;
    const isSphere = v === 'bubble' || v === 'aegis' || v === 'astral' || v === 'ward' || v === 'divine' || v === 'shieldWall';
    if (isSphere) this.sph.release(loop.vol);
    else {
      this.cyl.release(loop.vol);
      this.cyl.release(loop.vol2);
    }
    if (v === 'smash') this.decalAlpha.release(loop.decal, 0.6);
    else this.decalAdd.release(loop.decal, 0.4);
  }

  // =========================================================================
  // Zones (ground areas with an ability look: Earthquake, Consecrated Ground, hostile pools)
  // =========================================================================

  private syncZones(areas: readonly RenderArea[]): void {
    const stamp = this.frameNo;
    for (const a of areas) {
      if (a.kind !== 'zone') continue;
      let z = this.zones.get(a.id);
      if (!z) {
        z = this.createZone(a);
        this.zones.set(a.id, z);
        this.zoneList.push(z);
      }
      z.area = a.area;
      z.seen = stamp;
    }
    let w = 0;
    for (let i = 0; i < this.zoneList.length; i++) {
      const z = this.zoneList[i]!;
      if (z.seen === stamp) {
        this.zoneList[w++] = z;
        continue;
      }
      this.zones.delete(z.id);
      this.decalAlpha.release(z.decal, 0.6);
      this.decalAdd.release(z.decal2, 0.5);
      if (z.style === 'star' && z.spec) {
        // +basekits: the orb winks out into the end burst.
        this.sph.release(z.vol ?? -1, 0.12);
        this.sph.release(z.vol2 ?? -1, 0.25);
        this.starBloom(z, z.spec);
      }
    }
    this.zoneList.length = w;
  }

  // +basekits: Wandering Star -------------------------------------------------

  /** The live star orb nearest a unit (within its reach), in world space. */
  private starNear(x: number, y: number): ZoneFx | null {
    let best: ZoneFx | null = null;
    let bd = 7;
    for (const z of this.zoneList) {
      if (z.style !== 'star') continue;
      const d = Math.hypot((z.sx ?? z.area.origin.x) - x, (z.sy ?? z.area.origin.y) - y);
      if (d < bd) {
        bd = d;
        best = z;
      }
    }
    return best;
  }

  /** A crackling arc from the orb to an enemy it zapped (end-burst hits get a flash instead). */
  private starArc(targetId: UnitId, view: FxView): void {
    const u = view.units.get(targetId);
    if (!u) return;
    const z = this.starNear(u.pos.x, u.pos.y);
    const spec = z?.spec;
    if (!z || !spec) return;
    const ox = z.sx ?? z.area.origin.x;
    const oy = z.sy ?? z.area.origin.y;
    V2.set(ox, groundY(ox, oy) + STAR_H, -oy);
    toThree(u, view.heightOf(u.id) * 0.55, V3);
    this.bolt(V2, V3, spec, 0.25, 0.14);
    this.burst(P.flash, PRIO.impact, 1, V3, spec.color, spec.accent, 1.0, 1);
    this.burst(P.boltlets, PRIO.impact, 4, V3, spec.color, spec.accent, 0.9, 1).radius = 0.5;
  }

  /** The big bloom where the star's path ends. */
  private starBloom(z: ZoneFx, spec: VfxSpec): void {
    const x = z.sx ?? z.area.origin.x;
    const y = z.sy ?? z.area.origin.y;
    const c = spec.color;
    const acc = spec.accent;
    const p = V1.set(x, groundY(x, y) + STAR_H, -y);
    this.decalAdd.spawn(DECAL.ring, x, y, 8, c[0], c[1], c[2], 0.85, 0.8);
    this.decalAdd.spawn(DECAL.flash, x, y, 6, acc[0], acc[1], acc[2], 0.6, 0.6);
    this.sph.spawn(VOL.divine, x, STAR_H, -y, 2.4, 2.4, 1, acc[0], acc[1], acc[2], 1, 0.6, 2);
    this.sph.spawn(VOL.bubble, x, 1.2, -y, 4.2, 3.2, 1, c[0], c[1], c[2], 0.8, 0.9, -1);
    this.burst(P.flash, PRIO.impact, 1, p, c, acc, 5, 1);
    this.burst(P.glow, PRIO.impact, 1, p, c, acc, 6, 1);
    this.burst(P.ring, PRIO.impact, 1, p, c, acc, 5, 1);
    this.burst(P.stars, PRIO.impact, 48, p, c, acc, 1.3, 1).radius = 2.0;
    this.burst(P.boltlets, PRIO.impact, 18, p, c, acc, 1.5, 1).radius = 3.0;
    const q = this.burst(P.sparks, PRIO.impact, 30, p, c, acc, 1.3, 1);
    q.dy = 0.2;
    this.shake.add(0.12);
    this.onStarBurst?.(x, y);
  }

  private createZone(a: RenderArea): ZoneFx {
    const z: ZoneFx = { id: a.id, style: null, spec: null, area: a.area, decal: -1, decal2: -1, acc: 0, pulse: 0.2, seen: 0 };
    if (!a.abilityId) return z;
    const info = this.info(a.abilityId);
    const spec = resolveVfx(a.abilityId, info, a.hostile);
    let style: ZoneStyle | null = spec.zone ?? null;
    if (!style && a.hostile) style = (info?.school ?? 'nature') === 'nature' ? 'venomPool' : 'embers';
    // Consecrated Ground places two overlapping zones (damage + DR); decorate only one.
    for (const o of this.zoneList) {
      if (o.style && o.style === style && o.spec?.key === spec.key && Math.hypot(o.area.origin.x - a.area.origin.x, o.area.origin.y - a.area.origin.y) < 0.6) return z;
    }
    z.style = style;
    z.spec = spec;
    const R = a.area.shape.type === 'circle' ? a.area.shape.radius : a.area.shape.type === 'ring' ? a.area.shape.outer : 4;
    const o = a.area.origin;
    const c = spec.color;
    const acc = spec.accent;
    if (style === 'quake') {
      z.decal = this.decalAlpha.spawn(DECAL.cracks, o.x, o.y, R, CRACK[0], CRACK[1], CRACK[2], 0.6, 1.0, undefined, 0.5);
      z.decal2 = this.decalAdd.spawn(DECAL.flash, o.x, o.y, R, acc[0], acc[1], acc[2], 0.12 * FRIENDLY_ALPHA, 1.0, undefined, 0.3);
      this.decalAdd.spawn(DECAL.ring, o.x, o.y, R, acc[0], acc[1], acc[2], 0.45 * FRIENDLY_ALPHA, 0.5);
      this.burst(P.dustRing, PRIO.decor, 14, V1.set(o.x, groundY(o.x, o.y) + 0.3, -o.y), DUST, DUST, 1.2, 0.55).radius = R * 0.5;
    } else if (style === 'consecrate') {
      z.decal2 = this.decalAdd.spawn(DECAL.glyph, o.x, o.y, R, c[0], c[1], c[2], 0.22 * FRIENDLY_ALPHA, 1.0, undefined, 0.5);
    } else if (style === 'star') {
      // +basekits: Wandering Star launches from the caster with a flash.
      z.sx = o.x;
      z.sy = o.y;
      const p = V1.set(o.x, groundY(o.x, o.y) + STAR_H, -o.y);
      this.burst(P.flash, PRIO.impact, 1, p, c, acc, 2.6, 1);
      this.burst(P.ring, PRIO.impact, 1, p, c, acc, 2.0, 1);
      this.burst(P.stars, PRIO.impact, 18, p, c, acc, 1.0, 1).radius = 0.8;
      // A held bright core and a softer halo that ride the orb (moved every frame).
      z.vol = this.sph.spawn(VOL.divine, o.x, STAR_H, -o.y, 0.55, 0.55, 1, acc[0], acc[1], acc[2], 1, 0.4, 2.5, 0.5);
      z.vol2 = this.sph.spawn(VOL.bubble, o.x, STAR_H, -o.y, 1.1, 1.1, 1, c[0], c[1], c[2], 0.65, 0.4, -1.5, 0.5);
    } else if (style === 'snare') {
      // +basekits: an armed trap: a faint ring on the floor until it springs.
      z.decal2 = this.decalAdd.spawn(DECAL.ring, o.x, o.y, R, c[0], c[1], c[2], 0.35 * FRIENDLY_ALPHA, 1.0, undefined, 0.5);
    } else if (style === 'venomPool' && !a.hostile) {
      // +rogue-specs: a friendly toxin pool / gas cloud (Venomwright): a soft stain and its edge.
      // A darker stain (normal blending reads on bright grass), a lit edge, and a ring pulse on creation.
      z.decal = this.decalAlpha.spawn(DECAL.scorch, o.x, o.y, R, c[0] * 0.35, c[1] * 0.45, c[2] * 0.2, 0.5, 1.0, undefined, 0.5);
      z.decal2 = this.decalAdd.spawn(DECAL.ring, o.x, o.y, R, c[0], c[1], c[2], 0.3 * FRIENDLY_ALPHA, 1.0, undefined, 0.4);
      this.decalAdd.spawn(DECAL.flash, o.x, o.y, R, acc[0], acc[1], acc[2], 0.35 * FRIENDLY_ALPHA, 0.6);
      this.burst(P.smoke, PRIO.decor, 8, V1.set(o.x, groundY(o.x, o.y) + 0.4, -o.y), c, c, 1.2, 0.35).radius = R * 0.6;
    }
    return z;
  }

  private updateZones(dt: number, view: FxView): void {
    for (const z of this.zoneList) {
      if (!z.style || !z.spec) continue;
      const o = z.area.origin;
      const R = z.area.shape.type === 'circle' ? z.area.shape.radius : z.area.shape.type === 'ring' ? z.area.shape.outer : 4;
      const c = z.spec.color;
      const acc = z.spec.accent;
      z.pulse -= dt;
      z.acc += dt;
      switch (z.style) {
        case 'quake':
          if (z.pulse <= 0) {
            z.pulse = 1.0;
            this.decalAdd.spawn(DECAL.ring, o.x, o.y, R, acc[0], acc[1], acc[2], 0.35 * FRIENDLY_ALPHA, 0.6);
            for (let i = 0; i < 5; i++) {
              samplePointInShape(z.area, rnd(), rnd(), P2);
              V1.set(P2.x, groundY(P2.x, P2.y) + 0.2, -P2.y);
              this.burst(P.debris, PRIO.loop, 2, V1, DEBRIS, DEBRIS, 0.9, 1).radius = 0.4;
              this.burst(P.dust, PRIO.decor, 2, V1, DUST, DUST, 1.0, 0.5).radius = 0.6;
            }
            this.burst(P.dustRing, PRIO.decor, 10, V1.set(o.x, groundY(o.x, o.y) + 0.3, -o.y), DUST, DUST, 1.1, 0.5).radius = R * 0.85;
            const d = Math.hypot(o.x - view.focus.x, o.y - view.focus.y);
            this.shake.add(0.07 * clamp01(1 - d / 20));
          }
          break;
        case 'consecrate':
          if (z.acc > 0.18) {
            z.acc = 0;
            samplePointInShape(z.area, rnd(), rnd(), P2);
            const q = this.burst(P.stars, PRIO.decor, 1, V1.set(P2.x, groundY(P2.x, P2.y) + 0.25, -P2.y), c, acc, 0.5, FRIENDLY_ALPHA * 0.8);
            q.speed = 1.5;
          }
          break;
        case 'star': {
          // +basekits: a bright core that drifts with the sim area (smoothed between ticks), a
          // trailing ribbon of sparkles and stray little arcs; a soft glow follows on the floor.
          const k = Math.min(1, dt * 14);
          z.sx = (z.sx ?? o.x) + (o.x - (z.sx ?? o.x)) * k;
          z.sy = (z.sy ?? o.y) + (o.y - (z.sy ?? o.y)) * k;
          const x = z.sx;
          const y = z.sy;
          const bob = Math.sin(this.now * 7) * 0.12;
          const p = V1.set(x, groundY(x, y) + STAR_H + bob, -y);
          this.sph.move(z.vol ?? -1, x, STAR_H + bob, -y);
          this.sph.move(z.vol2 ?? -1, x, STAR_H + bob, -y);
          if (z.acc > 0.03) {
            z.acc = 0;
            this.burst(P.glow, PRIO.loop, 1, p, c, acc, 3.2, 1);
            this.burst(P.flash, PRIO.loop, 1, p, acc, acc, 1.3, 1);
            // The sparkle ribbon: stars shed behind the orb that hang and fade where it passed.
            const t = this.burst(P.stars, PRIO.loop, 6, p, c, acc, 0.85, 1);
            t.radius = 0.55;
            t.speed = 0.35;
            this.burst(P.motes, PRIO.decor, 3, p, c, acc, 0.7, 0.9).radius = 0.7;
            this.decalAdd.spawn(DECAL.flash, x, y, 3.2, c[0], c[1], c[2], 0.28, 0.14);
          }
          if (z.pulse <= 0) {
            z.pulse = 0.1;
            this.burst(P.boltlets, PRIO.loop, 3, p, c, acc, 1.1, 1).radius = 1.1;
          }
          break;
        }
        case 'snare':
          if (z.acc > 0.6) {
            z.acc = 0;
            this.burst(P.motes, PRIO.decor, 2, V1.set(o.x, groundY(o.x, o.y) + 0.2, -o.y), c, acc, 0.6, FRIENDLY_ALPHA).radius = R * 0.6;
          }
          break;
        case 'venomPool':
        case 'embers':
          if (z.acc > 0.3) {
            z.acc = 0;
            samplePointInShape(z.area, rnd(), rnd(), P2);
            V1.set(P2.x, groundY(P2.x, P2.y) + 0.15, -P2.y);
            if (z.style === 'venomPool') this.burst(P.drips, PRIO.decor, 2, V1, c, acc, 0.7, 0.75).speed = 0.8;
            else this.burst(P.embers, PRIO.decor, 1, V1, c, acc, 0.7, 0.75);
          }
          break;
      }
    }
  }

  // =========================================================================
  // Per-frame update
  // =========================================================================

  update(dt: number, view: FxView): void {
    this.now += dt;
    this.uTime.value = this.now;
    this.clones.update(dt, view.units); // +rogue-specs
    // Scheduled callbacks (chain hops, delayed heals).
    if (this.scheduled.length) {
      const due: Scheduled[] = [];
      let w = 0;
      for (const s of this.scheduled) {
        if (s.at <= this.now) due.push(s);
        else this.scheduled[w++] = s;
      }
      this.scheduled.length = w;
      for (const s of due) s.run();
    }
    // +impact: deaths whose killing blow never landed (unit vanished mid-flight) play anyway.
    if (this.deathWait.size) for (const [id, w] of this.deathWait) if (this.now - w.at > 1) this.releaseKill(id, view);
    // Text safety net: anything held longer than any flight (unit vanished mid-chain, etc.) shows now.
    if (this.heldText.size) {
      for (const [e, at] of this.heldText) {
        if (this.now - at > 1.2) {
          this.heldText.delete(e);
          this.textOut.push(e);
        }
      }
    }
    this.updateCasts(dt, view);
    this.updateAuras(dt, view);
    this.updateZones(dt, view);
    this.updateProjectiles(dt, view);
    this.updateBolts(dt);
    this.updateSwings(dt);
    this.updateBeams(dt, view);

    this.emit.flush(view.camera);
    this.addPool.update(dt);
    this.alphaPool.update(dt);
    this.addBatch.sync();
    this.alphaBatch.sync();
    view.camera.getWorldPosition(V1);
    this.trails.build(V1);
    this.arcs.build(V1);
    this.decalAdd.update(dt);
    this.decalAlpha.update(dt);
    this.cyl.update(dt);
    this.sph.update(dt);
  }

  private updateProjectiles(dt: number, view: FxView): void {
    let w = 0;
    this.arrowN = 0;
    for (let i = 0; i < this.projectiles.length; i++) {
      const pj = this.projectiles[i]!;
      const sp = pj.spec;
      const al = this.alphaOf(sp);
      const tgt = view.units.get(pj.targetId);
      if (!pj.arrived) {
        pj.t += dt / pj.dur;
        const t = Math.min(1, pj.t);
        if (tgt) toThree(tgt, view.heightOf(tgt.id) * 0.55, V2);
        else V2.copy(pj.last);
        const k = t * (0.7 + 0.3 * t);
        const pos = V1.lerpVectors(pj.from, V2, k);
        pos.y += Math.sin(k * Math.PI) * pj.arc;
        V3.subVectors(pos, pj.last);
        pj.last.copy(pos);
        this.pushTrail(pj, pos);
        // Head + trail particles.
        if (sp.projectile === 'arrow' && this.arrowN < ARROW_MAX && V3.lengthSq() > 1e-8) {
          // +classes: the arrow points along its flight (velocity this frame).
          ARROW_Q.setFromUnitVectors(ARROW_FWD, V5.copy(V3).normalize());
          ARROW_M.compose(pos, ARROW_Q, ARROW_S.setScalar(sp.scale));
          this.arrows.setMatrixAt(this.arrowN++, ARROW_M);
        } else if (sp.projectile !== 'wind') {
          // Head: two short-lived glows re-emitted every frame (≈2 frames each) so it never smears.
          const heavy = sp.projectile === 'lava' || sp.projectile === 'fire' ? 1.45 : 1;
          this.burst(P.glow, pj.prio, 1, pos, sp.color, sp.accent, 0.85 * sp.scale * heavy, al * 0.8).life = 0.12;
          this.burst(P.flash, pj.prio, 1, pos, sp.color, sp.accent, 0.45 * sp.scale * heavy, al).life = 0.25;
        }
        pj.acc += dt;
        const step = 1 / 70;
        while (pj.acc >= step) {
          pj.acc -= step;
          switch (sp.projectile) {
            case 'lava':
            case 'fire':
              this.burst(P.embers, PRIO.loop, 1, pos, sp.color, sp.accent, 1.6, al).speed = 0.5;
              if (rnd() < 0.3) this.burst(P.flames, PRIO.loop, 1, pos, sp.color, sp.accent, 0.6, al).speed = 0.3;
              if (rnd() < 0.2) this.burst(P.smoke, PRIO.decor, 1, pos, SMOKE, SMOKE, 0.5, 0.35).speed = 0.3;
              break;
            case 'holy':
              if (rnd() < 0.5) this.burst(P.stars, PRIO.loop, 1, pos, sp.color, sp.accent, 0.45, al).radius = 0.15;
              break;
            case 'venom':
              if (rnd() < 0.6) this.burst(P.drips, PRIO.loop, 1, pos, sp.color, sp.accent, 0.8, al).speed = 0.3;
              break;
            case 'shadow':
              if (rnd() < 0.35) this.burst(P.wisps, PRIO.loop, 1, pos, sp.color, sp.accent, 0.4, al * 0.7);
              break;
            case 'arrow':
              if (rnd() < 0.25) this.burst(P.streaks, PRIO.loop, 1, pos, sp.color, sp.accent, 0.4, al * 0.5).radius = 0.1;
              break;
            case 'wind': {
              const q = this.burst(P.streaks, PRIO.loop, 1, pos, sp.color, sp.accent, 0.8, al);
              q.dx = V3.x;
              q.dy = V3.y;
              q.dz = V3.z;
              q.radius = 0.25;
              q.speed = 0.4;
              break;
            }
            default:
              if (rnd() < 0.5) this.burst(P.embers, PRIO.loop, 1, pos, sp.color, sp.accent, 0.7, al).speed = 0.3;
          }
        }
        if (pj.t >= 1) {
          pj.arrived = true;
          if (pj.hits.length === 0 && tgt && sp.impact !== 'none') this.impactOn(sp.impact, sp, tgt.id, pj.sourceId, false, view, pj.prio);
          for (const h of pj.hits) this.applyHit(pj.abilityId, pj.sourceId, h, view, false);
        }
      } else {
        pj.fade += dt;
        this.pushTrail(pj, pj.last);
      }
      if (pj.arrived && pj.fade > 0.2) {
        if (pj.strip >= 0) this.trails.free(pj.strip);
        continue;
      }
      this.writeTrail(pj, al);
      this.projectiles[w++] = pj;
    }
    this.projectiles.length = w;
    this.arrows.count = this.arrowN;
    if (this.arrowN) this.arrows.instanceMatrix.needsUpdate = true;
  }

  private pushTrail(pj: Projectile, p: THREE.Vector3): void {
    pj.histHead = (pj.histHead + 1) % TRAIL_POINTS;
    pj.hist[pj.histHead * 3] = p.x;
    pj.hist[pj.histHead * 3 + 1] = p.y;
    pj.hist[pj.histHead * 3 + 2] = p.z;
    pj.histN = Math.min(TRAIL_POINTS, pj.histN + 1);
  }

  private writeTrail(pj: Projectile, al: number): void {
    if (pj.strip < 0) return;
    // Ribbons are clipped by length (frame-rate independent) so bolts read as projectiles, not beams.
    const wind = pj.spec.projectile === 'wind';
    const maxLen = (wind ? 4.5 : pj.spec.projectile === 'lava' ? 3 : 2.2) * pj.spec.scale;
    const w0 = (wind ? 0.18 : pj.spec.projectile === 'lava' ? 0.55 : 0.36) * pj.spec.scale;
    const fade = pj.arrived ? Math.max(0, 1 - pj.fade / 0.2) : 1;
    let n = 0;
    let len = 0;
    let px = 0;
    let py = 0;
    let pz = 0;
    for (let j = 0; j < pj.histN; j++) {
      const idx = ((pj.histHead - j + TRAIL_POINTS) % TRAIL_POINTS) * 3;
      const x = pj.hist[idx]!;
      const y = pj.hist[idx + 1]!;
      const z = pj.hist[idx + 2]!;
      if (j > 0) len += Math.hypot(x - px, y - py, z - pz);
      px = x;
      py = y;
      pz = z;
      this.trails.setPoint(pj.strip, j, x, y, z, 0, 0);
      n = j + 1;
      if (len >= maxLen) break;
    }
    for (let j = 0; j < n; j++) {
      const u = n > 1 ? j / (n - 1) : 0;
      const o = pj.strip * TRAIL_POINTS + j;
      this.trails.width[o] = w0 * (1 - u) ** 0.8;
      this.trails.alpha[o] = 0.85 * al * fade * (1 - u) ** 1.3;
    }
    this.trails.count[pj.strip] = n;
  }

  private updateBolts(dt: number): void {
    let w = 0;
    for (let i = 0; i < this.bolts.length; i++) {
      const b = this.bolts[i]!;
      b.age += dt;
      if (b.age >= b.life) {
        this.arcs.free(b.strip);
        this.arcs.free(b.branch);
        continue;
      }
      this.bolts[w++] = b;
      b.jitter -= dt;
      const t = b.age / b.life;
      // +impact: hot for the first third, then a slower afterglow (long-life arcs linger a beat).
      const fade = b.life > 0.3 ? (t < 0.3 ? 1 - t * 0.5 : 0.85 * (1 - (t - 0.3) / 0.7) ** 1.6) : 1 - t;
      const alpha = b.alpha * fade * (0.75 + 0.25 * rnd());
      if (b.jitter <= 0) {
        b.jitter = t < 0.3 ? 0.035 : 0.06;
        this.jag(b.strip, b.a, b.b, BOLT_POINTS, b.width, alpha);
        if (b.branch >= 0) {
          const j = 0.3 + rnd() * 0.4;
          V2.lerpVectors(b.a, b.b, j);
          V3.subVectors(b.b, b.a).multiplyScalar(0.3);
          V3.x += (rnd() - 0.5) * V3.length();
          V3.y += (rnd() - 0.3) * V3.length() * 0.6;
          V3.z += (rnd() - 0.5) * V3.length();
          V3.add(V2);
          this.jag(b.branch, V2, V3, 8, b.width * 0.6, alpha * 0.8);
        }
      } else {
        // Flicker alpha between re-randomisations.
        const n = this.arcs.count[b.strip]!;
        for (let j = 0; j < n; j++) this.arcs.alpha[b.strip * BOLT_POINTS + j] = alpha * (j === 0 || j === n - 1 ? 0.6 : 1);
      }
    }
    this.bolts.length = w;
  }

  /** Jagged polyline between a and b with mid-span displacement. */
  private jag(strip: number, a: THREE.Vector3, b: THREE.Vector3, n: number, width: number, alpha: number): void {
    if (strip < 0) return;
    V4.subVectors(b, a);
    const len = V4.length();
    if (len < 1e-4) return;
    const amp = Math.min(1.1, len * 0.1);
    // Two perpendiculars.
    const p1 = V3.crossVectors(V4, UP);
    if (p1.lengthSq() < 1e-6) p1.set(1, 0, 0);
    p1.normalize();
    const p2x = V4.y * p1.z - V4.z * p1.y;
    const p2y = V4.z * p1.x - V4.x * p1.z;
    const p2z = V4.x * p1.y - V4.y * p1.x;
    const p2l = Math.hypot(p2x, p2y, p2z) || 1;
    for (let j = 0; j < n; j++) {
      const u = j / (n - 1);
      const env = Math.sin(Math.PI * u);
      const o1 = (rnd() - 0.5) * 2 * amp * env;
      const o2 = (rnd() - 0.5) * 2 * amp * env;
      this.arcs.setPoint(
        strip,
        j,
        a.x + V4.x * u + p1.x * o1 + (p2x / p2l) * o2,
        a.y + V4.y * u + p1.y * o1 + (p2y / p2l) * o2,
        a.z + V4.z * u + p1.z * o1 + (p2z / p2l) * o2,
        width * (0.65 + 0.35 * env),
        alpha * (j === 0 || j === n - 1 ? 0.6 : 1),
      );
    }
    this.arcs.count[strip] = n;
  }

  private updateSwings(dt: number): void {
    let w = 0;
    for (let i = 0; i < this.swings.length; i++) {
      const s = this.swings[i]!;
      s.age += dt;
      if (s.age >= s.life) {
        this.arcs.free(s.strip);
        continue;
      }
      this.swings[w++] = s;
      const t = s.age / s.life;
      const head = easeOutCubic(Math.min(1, t / 0.55));
      const tail = easeOutCubic(clamp01((t - 0.15) / 0.7));
      const fade = Math.sqrt(1 - t);
      const n = SWING_POINTS;
      for (let j = 0; j < n; j++) {
        const u = j / (n - 1); // 0 tail .. 1 head
        const ph = tail + (head - tail) * u;
        swingPoint(s, ph, V1);
        this.arcs.setPoint(s.strip, j, V1.x, V1.y, V1.z, s.width * (0.15 + 0.85 * u), 0.85 * fade * u ** 0.7);
      }
      this.arcs.count[s.strip] = n;
    }
    this.swings.length = w;
  }

  private updateBeams(dt: number, view: FxView): void {
    let w = 0;
    for (let i = 0; i < this.beams.length; i++) {
      const b = this.beams[i]!;
      b.age += dt;
      if (b.age >= b.life) {
        this.trails.free(b.strip);
        continue;
      }
      this.beams[w++] = b;
      // Follow the endpoints while they exist.
      if (b.sourceId) {
        const h = view.handOf(b.sourceId, V1);
        if (h) b.a.copy(h);
      }
      if (b.targetId) {
        const u = view.units.get(b.targetId);
        if (u) toThree(u, view.heightOf(u.id) * 0.55, b.b);
      }
      const t = b.age / b.life;
      const n = 10;
      V4.subVectors(b.b, b.a);
      for (let j = 0; j < n; j++) {
        const u = j / (n - 1);
        const wob = b.dash ? 0 : Math.sin(u * 9 - this.now * 22) * 0.08 * Math.sin(Math.PI * u);
        const alpha = b.dash ? (1 - t) * u : (1 - t) * 0.9;
        this.trails.setPoint(b.strip, j, b.a.x + V4.x * u, b.a.y + V4.y * u + wob, b.a.z + V4.z * u, b.width * (b.dash ? 0.3 + 0.7 * u : 1), alpha);
      }
      this.trails.count[b.strip] = n;
    }
    this.beams.length = w;
  }

  clear(): void {
    this.clones.clear(); // +rogue-specs
    for (const p of this.projectiles) this.trails.free(p.strip);
    this.projectiles = [];
    this.bolts = [];
    this.swings = [];
    this.beams = [];
    this.casts = [];
    this.auras = [];
    this.auraIndex.clear();
    this.zones.clear();
    this.zoneList = [];
    this.telegraphs.clear();
    this.killHeld.clear();
    this.deathWait.clear();
    this.scheduled = [];
    for (const e of this.heldText.keys()) this.textOut.push(e);
    this.heldText.clear();
    this.trails.clear();
    this.arcs.clear();
    this.emit.clear();
    this.addPool.clear();
    this.alphaPool.clear();
    this.decalAdd.clear();
    this.decalAlpha.clear();
    this.cyl.clear();
    this.sph.clear();
  }

  dispose(): void {
    this.clear();
    this.clones.dispose(); // +rogue-specs
    this.addBatch.dispose();
    this.alphaBatch.dispose();
    this.arrowGeo.dispose();
    this.arrowMat.dispose();
    this.arrows.dispose();
    this.trails.dispose();
    this.arcs.dispose();
    this.decalAdd.dispose();
    this.decalAlpha.dispose();
    this.cyl.dispose();
    this.sph.dispose();
    this.cylGeo.dispose();
    this.sphGeo.dispose();
    this.atlas.dispose();
    this.group.removeFromParent();
  }

  /** Debug counters. */
  stats(): { particles: number; dropped: number; strips: number; loops: number } {
    return { particles: this.addPool.liveCount + this.alphaPool.liveCount, dropped: this.emit.dropped, strips: this.projectiles.length + this.bolts.length + this.swings.length + this.beams.length, loops: this.casts.length + this.auras.length };
  }
}

const CUE_ACC: [number, number, number] = [0, 0, 0];
const SLAM_COL: [number, number, number] = [0, 0, 0];
const SLAM_ACC: [number, number, number] = [0, 0, 0];
function mixRGB(a: RGB, b: RGB, t: number, out: [number, number, number]): RGB {
  out[0] = a[0] + (b[0] - a[0]) * t;
  out[1] = a[1] + (b[1] - a[1]) * t;
  out[2] = a[2] + (b[2] - a[2]) * t;
  return out;
}

const STORM_LOOP_SPEC: VfxSpec = ABILITY_VFX[ABILITY.stormcaller.lightningBolt]!;

/** Point on a swing arc at phase 0..1 (tail→head sweep). */
function swingPoint(s: Swing, ph: number, out: THREE.Vector3): THREE.Vector3 {
  const R = s.rad;
  switch (s.style) {
    case 'thrust': {
      const d = 0.2 + ph * (R + 0.7);
      return out.copy(s.c).addScaledVector(s.f, d).addScaledVector(s.r, (1 - ph) * 0.15);
    }
    case 'overhead': {
      // Steep diagonal chop (reads as a crescent from the high camera): high right → low left.
      const th = 1.35 - ph * 2.7;
      return out.copy(s.c).addScaledVector(s.f, Math.cos(th) * R * 0.95).addScaledVector(s.r, Math.sin(th) * R * 0.75).addScaledVector(UP, Math.sin(th) * R * 0.7);
    }
    case 'wide': {
      const th = 1.9 - ph * 3.8;
      return out.copy(s.c).addScaledVector(s.f, Math.cos(th) * R).addScaledVector(s.r, Math.sin(th) * R).addScaledVector(UP, -0.05 + Math.sin(th) * 0.12);
    }
    case 'spin': {
      const th = ph * Math.PI * 2;
      return out.copy(s.c).addScaledVector(s.f, Math.cos(th) * R).addScaledVector(s.r, Math.sin(th) * R);
    }
    default: {
      const th = 1.25 - ph * 2.5;
      return out.copy(s.c).addScaledVector(s.f, Math.cos(th) * R).addScaledVector(s.r, Math.sin(th) * R).addScaledVector(UP, Math.sin(th) * R * 0.35);
    }
  }
}

// ---------------------------------------------------------------------------------------------
// +classes: arrow mesh (Ranger projectiles)
// ---------------------------------------------------------------------------------------------

const ARROW_MAX = 48;
/** +volley: arrows per Arrowfall tick on a unit (fewer when many projectiles are already in flight). */
function abilityVolley(inFlight: number): number {
  return inFlight > 40 ? 0 : inFlight > 28 ? 1 : 3;
}
const ARROWFALL = 'ranger.arrowfall';
const ARROW_FWD = new THREE.Vector3(0, 0, 1);
const ARROW_Q = new THREE.Quaternion();
const ARROW_M = new THREE.Matrix4();
const ARROW_S = new THREE.Vector3();
const V5 = new THREE.Vector3();

/** A 1.1 yd arrow along +Z: wooden shaft, steel head, pale fletching (vertex colours, unlit). */
function arrowGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const paint = (g: THREE.BufferGeometry, hex: number): THREE.BufferGeometry => {
    const c = new THREE.Color(hex);
    const n = g.attributes['position']!.count;
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) col.set([c.r, c.g, c.b], i * 3);
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.deleteAttribute('uv');
    g.deleteAttribute('normal');
    return g;
  };
  parts.push(paint(new THREE.CylinderGeometry(0.022, 0.022, 1.0, 5).rotateX(Math.PI / 2), 0x9a6b3c));
  parts.push(paint(new THREE.ConeGeometry(0.06, 0.18, 5).rotateX(Math.PI / 2).translate(0, 0, 0.58), 0xd8dde6));
  for (const r of [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3]) {
    parts.push(paint(new THREE.PlaneGeometry(0.09, 0.2).translate(0.05, 0, 0).rotateZ(r).translate(0, 0, -0.42), 0xf2ece0));
  }
  const g = mergeArrowParts(parts);
  return g;
}

function mergeArrowParts(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const ni = parts.map((p) => (p.index ? p.toNonIndexed() : p));
  let n = 0;
  for (const p of ni) n += p.attributes['position']!.count;
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  let o = 0;
  for (const p of ni) {
    pos.set(p.attributes['position']!.array as Float32Array, o * 3);
    col.set(p.attributes['color']!.array as Float32Array, o * 3);
    o += p.attributes['position']!.count;
  }
  for (const p of [...parts, ...ni]) p.dispose();
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}
